// Central data store for ArchSim — all component definitions, presets, and mock data

export type ComponentGroup =
  | 'Clients' | 'Edge' | 'Traffic' | 'Compute' | 'Cache' |'Database'| 'Storage' | 'Search' | 'Messaging' | 'Distributed' |'Reliability' | 'Security' | 'Observability' | 'Specialized';

export interface ComponentDef {
  id: string;
  name: string;
  group: ComponentGroup;
  icon: string;
  color: string;
  description: string;
  defaultLatency: number; // ms
  defaultThroughput: number; // RPS
  defaultErrorRate: number; // %
}

export interface CanvasNode {
  id: string;
  defId: string;
  x: number;
  y: number;
  label: string;
  status: 'idle' | 'active' | 'degraded' | 'failed';
  metrics: {
    rps: number;
    latency: number;
    errorRate: number;
    cpuLoad: number;
  };
  config: Record<string, string | number | boolean>;
}

export interface CanvasEdge {
  id: string;
  sourceId: string;
  targetId: string;
  protocol: 'HTTP' | 'gRPC' | 'TCP' | 'AMQP' | 'WebSocket';
  animated: boolean;
}

export interface SavedArchitecture {
  id: string;
  name: string;
  description: string;
  nodes: CanvasNode[];
  edges: CanvasEdge[];
  savedAt: string;
  tags: string[];
}

export const COMPONENT_GROUPS: Record<ComponentGroup, { color: string; cssVar: string }> = {
  Clients:       { color: '#818cf8', cssVar: 'var(--node-client)' },
  Edge:          { color: '#34d399', cssVar: 'var(--node-edge)' },
  Traffic:       { color: '#f472b6', cssVar: 'var(--node-traffic)' },
  Compute:       { color: '#60a5fa', cssVar: 'var(--node-compute)' },
  Cache:         { color: '#fbbf24', cssVar: 'var(--node-cache)' },
  Database:      { color: '#a78bfa', cssVar: 'var(--node-database)' },
  Storage:       { color: '#fb923c', cssVar: 'var(--node-storage)' },
  Search:        { color: '#2dd4bf', cssVar: 'var(--node-search)' },
  Messaging:     { color: '#e879f9', cssVar: 'var(--node-messaging)' },
  Distributed:   { color: '#38bdf8', cssVar: 'var(--node-distributed)' },
  Reliability:   { color: '#4ade80', cssVar: 'var(--node-reliability)' },
  Security:      { color: '#f43f5e', cssVar: 'var(--node-security)' },
  Observability: { color: '#a3e635', cssVar: 'var(--node-observability)' },
  Specialized:   { color: '#c084fc', cssVar: 'var(--node-specialized)' },
};

export const ALL_COMPONENTS: ComponentDef[] = [
  // Clients
  { id: 'web-browser', name: 'Web Browser', group: 'Clients', icon: '🌐', color: '#818cf8', description: 'End-user HTTP/HTTPS client originating web traffic', defaultLatency: 0, defaultThroughput: 10000, defaultErrorRate: 0 },
  { id: 'mobile-app', name: 'Mobile App', group: 'Clients', icon: '📱', color: '#818cf8', description: 'iOS/Android native app client', defaultLatency: 0, defaultThroughput: 8000, defaultErrorRate: 0 },
  { id: 'api-client', name: 'API Client', group: 'Clients', icon: '🔌', color: '#818cf8', description: 'Programmatic API consumer (SDK, REST, GraphQL)', defaultLatency: 0, defaultThroughput: 5000, defaultErrorRate: 0 },
  { id: 'iot-device', name: 'IoT Device', group: 'Clients', icon: '🔧', color: '#818cf8', description: 'Embedded device sending telemetry data', defaultLatency: 0, defaultThroughput: 50000, defaultErrorRate: 0.5 },
  { id: 'desktop-app', name: 'Desktop App', group: 'Clients', icon: '🖥️', color: '#818cf8', description: 'Electron or native desktop application', defaultLatency: 0, defaultThroughput: 2000, defaultErrorRate: 0 },
  { id: 'third-party', name: 'Third-Party Service', group: 'Clients', icon: '🤝', color: '#818cf8', description: 'External service consuming your API', defaultLatency: 50, defaultThroughput: 3000, defaultErrorRate: 1 },

  // Edge
  { id: 'cdn', name: 'CDN', group: 'Edge', icon: '🌍', color: '#34d399', description: 'Content Delivery Network — caches static assets at edge PoPs', defaultLatency: 5, defaultThroughput: 100000, defaultErrorRate: 0.01 },
  { id: 'edge-cache', name: 'Edge Cache', group: 'Edge', icon: '⚡', color: '#34d399', description: 'Regional cache layer at network edge', defaultLatency: 2, defaultThroughput: 80000, defaultErrorRate: 0.01 },
  { id: 'dns', name: 'DNS', group: 'Edge', icon: '🗺️', color: '#34d399', description: 'Domain Name System resolution', defaultLatency: 1, defaultThroughput: 200000, defaultErrorRate: 0 },
  { id: 'anycast', name: 'Anycast Router', group: 'Edge', icon: '📡', color: '#34d399', description: 'Routes traffic to nearest datacenter via anycast IP', defaultLatency: 3, defaultThroughput: 500000, defaultErrorRate: 0 },

  // Traffic
  { id: 'load-balancer', name: 'Load Balancer', group: 'Traffic', icon: '⚖️', color: '#f472b6', description: 'Distributes incoming traffic across backend instances', defaultLatency: 1, defaultThroughput: 50000, defaultErrorRate: 0.01 },
  { id: 'api-gateway', name: 'API Gateway', group: 'Traffic', icon: '🚪', color: '#f472b6', description: 'Single entry point — auth, rate limiting, routing', defaultLatency: 5, defaultThroughput: 30000, defaultErrorRate: 0.1 },
  { id: 'reverse-proxy', name: 'Reverse Proxy', group: 'Traffic', icon: '🔄', color: '#f472b6', description: 'Nginx/Envoy — TLS termination, request routing', defaultLatency: 2, defaultThroughput: 40000, defaultErrorRate: 0.01 },
  { id: 'service-mesh', name: 'Service Mesh', group: 'Traffic', icon: '🕸️', color: '#f472b6', description: 'Istio/Linkerd — mTLS, traffic shaping, observability', defaultLatency: 3, defaultThroughput: 20000, defaultErrorRate: 0.05 },
  { id: 'rate-limiter', name: 'Rate Limiter', group: 'Traffic', icon: '🚦', color: '#f472b6', description: 'Token bucket / sliding window rate limiting', defaultLatency: 1, defaultThroughput: 100000, defaultErrorRate: 0 },
  { id: 'traffic-mirror', name: 'Traffic Mirror', group: 'Traffic', icon: '🪞', color: '#f472b6', description: 'Duplicates live traffic for shadow testing', defaultLatency: 0, defaultThroughput: 50000, defaultErrorRate: 0 },

  // Compute
  { id: 'web-server', name: 'Web Server', group: 'Compute', icon: '🖥️', color: '#60a5fa', description: 'HTTP server handling synchronous requests', defaultLatency: 20, defaultThroughput: 5000, defaultErrorRate: 0.5 },
  { id: 'app-server', name: 'App Server', group: 'Compute', icon: '⚙️', color: '#60a5fa', description: 'Business logic processing tier', defaultLatency: 30, defaultThroughput: 3000, defaultErrorRate: 0.5 },
  { id: 'microservice', name: 'Microservice', group: 'Compute', icon: '🧩', color: '#60a5fa', description: 'Independently deployable service with bounded context', defaultLatency: 15, defaultThroughput: 8000, defaultErrorRate: 0.3 },
  { id: 'serverless', name: 'Serverless Function', group: 'Compute', icon: '⚡', color: '#60a5fa', description: 'AWS Lambda / Cloud Functions — scales to zero', defaultLatency: 50, defaultThroughput: 10000, defaultErrorRate: 0.2 },
  { id: 'container', name: 'Container', group: 'Compute', icon: '📦', color: '#60a5fa', description: 'Docker container running a single process', defaultLatency: 10, defaultThroughput: 6000, defaultErrorRate: 0.2 },
  { id: 'k8s-pod', name: 'K8s Pod', group: 'Compute', icon: '☸️', color: '#60a5fa', description: 'Kubernetes pod — smallest deployable unit', defaultLatency: 10, defaultThroughput: 7000, defaultErrorRate: 0.1 },
  { id: 'worker', name: 'Background Worker', group: 'Compute', icon: '👷', color: '#60a5fa', description: 'Async job processor consuming from a queue', defaultLatency: 100, defaultThroughput: 500, defaultErrorRate: 0.5 },

  // Cache
  { id: 'redis', name: 'Redis', group: 'Cache', icon: '🔴', color: '#fbbf24', description: 'In-memory data store — sub-millisecond reads', defaultLatency: 0.5, defaultThroughput: 100000, defaultErrorRate: 0.01 },
  { id: 'memcached', name: 'Memcached', group: 'Cache', icon: '🟡', color: '#fbbf24', description: 'Simple distributed memory caching system', defaultLatency: 0.3, defaultThroughput: 150000, defaultErrorRate: 0.01 },
  { id: 'local-cache', name: 'Local Cache', group: 'Cache', icon: '💾', color: '#fbbf24', description: 'In-process LRU cache — zero network overhead', defaultLatency: 0.05, defaultThroughput: 1000000, defaultErrorRate: 0 },
  { id: 'distributed-cache', name: 'Distributed Cache', group: 'Cache', icon: '🌐', color: '#fbbf24', description: 'Cluster-wide cache with consistent hashing', defaultLatency: 1, defaultThroughput: 80000, defaultErrorRate: 0.05 },
  { id: 'cache-aside', name: 'Cache-Aside Pattern', group: 'Cache', icon: '↔️', color: '#fbbf24', description: 'Application manages cache population on miss', defaultLatency: 1, defaultThroughput: 50000, defaultErrorRate: 0.1 },

  // Database
  { id: 'postgres', name: 'PostgreSQL', group: 'Database', icon: '🐘', color: '#a78bfa', description: 'ACID-compliant relational database', defaultLatency: 5, defaultThroughput: 10000, defaultErrorRate: 0.1 },
  { id: 'mysql', name: 'MySQL', group: 'Database', icon: '🐬', color: '#a78bfa', description: 'Widely-used open-source relational database', defaultLatency: 4, defaultThroughput: 12000, defaultErrorRate: 0.1 },
  { id: 'mongodb', name: 'MongoDB', group: 'Database', icon: '🍃', color: '#a78bfa', description: 'Document store — flexible schema, horizontal scale', defaultLatency: 3, defaultThroughput: 20000, defaultErrorRate: 0.1 },
  { id: 'cassandra', name: 'Cassandra', group: 'Database', icon: '💫', color: '#a78bfa', description: 'Wide-column store — write-optimized, linear scale', defaultLatency: 2, defaultThroughput: 50000, defaultErrorRate: 0.2 },
  { id: 'dynamodb', name: 'DynamoDB', group: 'Database', icon: '⚡', color: '#a78bfa', description: 'Fully managed NoSQL — single-digit ms at any scale', defaultLatency: 1, defaultThroughput: 40000, defaultErrorRate: 0.05 },
  { id: 'db-replica', name: 'Read Replica', group: 'Database', icon: '📋', color: '#a78bfa', description: 'Read-only replica for offloading read traffic', defaultLatency: 6, defaultThroughput: 15000, defaultErrorRate: 0.1 },
  { id: 'db-shard', name: 'DB Shard', group: 'Database', icon: '🔀', color: '#a78bfa', description: 'Horizontal partition of database by shard key', defaultLatency: 5, defaultThroughput: 20000, defaultErrorRate: 0.2 },

  // Storage
  { id: 'object-storage', name: 'Object Storage', group: 'Storage', icon: '🪣', color: '#fb923c', description: 'S3-compatible blob storage for unstructured data', defaultLatency: 30, defaultThroughput: 5000, defaultErrorRate: 0.05 },
  { id: 'block-storage', name: 'Block Storage', group: 'Storage', icon: '🧱', color: '#fb923c', description: 'EBS-style persistent block device for VMs', defaultLatency: 5, defaultThroughput: 20000, defaultErrorRate: 0.02 },
  { id: 'nfs', name: 'NFS / File System', group: 'Storage', icon: '📁', color: '#fb923c', description: 'Network file system for shared file access', defaultLatency: 10, defaultThroughput: 3000, defaultErrorRate: 0.1 },
  { id: 'data-warehouse', name: 'Data Warehouse', group: 'Storage', icon: '🏭', color: '#fb923c', description: 'Columnar analytics store — BigQuery, Redshift', defaultLatency: 500, defaultThroughput: 100, defaultErrorRate: 0.1 },
  { id: 'data-lake', name: 'Data Lake', group: 'Storage', icon: '🏞️', color: '#fb923c', description: 'Raw data repository — S3 + Parquet + Glue', defaultLatency: 1000, defaultThroughput: 50, defaultErrorRate: 0.2 },

  // Search
  { id: 'elasticsearch', name: 'Elasticsearch', group: 'Search', icon: '🔍', color: '#2dd4bf', description: 'Distributed full-text search and analytics engine', defaultLatency: 8, defaultThroughput: 15000, defaultErrorRate: 0.1 },
  { id: 'opensearch', name: 'OpenSearch', group: 'Search', icon: '🔎', color: '#2dd4bf', description: 'Open-source fork of Elasticsearch', defaultLatency: 8, defaultThroughput: 15000, defaultErrorRate: 0.1 },
  { id: 'solr', name: 'Apache Solr', group: 'Search', icon: '☀️', color: '#2dd4bf', description: 'Enterprise search platform built on Lucene', defaultLatency: 12, defaultThroughput: 10000, defaultErrorRate: 0.2 },
  { id: 'typesense', name: 'Typesense', group: 'Search', icon: '⚡', color: '#2dd4bf', description: 'Fast typo-tolerant search engine', defaultLatency: 3, defaultThroughput: 25000, defaultErrorRate: 0.05 },

  // Messaging
  { id: 'kafka', name: 'Apache Kafka', group: 'Messaging', icon: '📨', color: '#e879f9', description: 'Distributed event streaming — millions of events/sec', defaultLatency: 5, defaultThroughput: 500000, defaultErrorRate: 0.01 },
  { id: 'rabbitmq', name: 'RabbitMQ', group: 'Messaging', icon: '🐇', color: '#e879f9', description: 'AMQP message broker — flexible routing', defaultLatency: 3, defaultThroughput: 50000, defaultErrorRate: 0.05 },
  { id: 'sqs', name: 'AWS SQS', group: 'Messaging', icon: '📬', color: '#e879f9', description: 'Managed message queue — at-least-once delivery', defaultLatency: 10, defaultThroughput: 30000, defaultErrorRate: 0.01 },
  { id: 'pubsub', name: 'Pub/Sub', group: 'Messaging', icon: '📢', color: '#e879f9', description: 'Google Cloud Pub/Sub — fan-out messaging', defaultLatency: 8, defaultThroughput: 100000, defaultErrorRate: 0.02 },
  { id: 'event-bus', name: 'Event Bus', group: 'Messaging', icon: '🚌', color: '#e879f9', description: 'Internal event bus for microservice decoupling', defaultLatency: 2, defaultThroughput: 20000, defaultErrorRate: 0.05 },
  { id: 'dead-letter', name: 'Dead Letter Queue', group: 'Messaging', icon: '💀', color: '#e879f9', description: 'Captures failed messages for inspection and retry', defaultLatency: 0, defaultThroughput: 100, defaultErrorRate: 0 },

  // Distributed
  { id: 'zookeeper', name: 'ZooKeeper', group: 'Distributed', icon: '🦁', color: '#38bdf8', description: 'Distributed coordination — leader election, config', defaultLatency: 2, defaultThroughput: 10000, defaultErrorRate: 0.01 },
  { id: 'etcd', name: 'etcd', group: 'Distributed', icon: '🔑', color: '#38bdf8', description: 'Distributed key-value store for cluster state', defaultLatency: 2, defaultThroughput: 10000, defaultErrorRate: 0.01 },
  { id: 'consul', name: 'Consul', group: 'Distributed', icon: '🏛️', color: '#38bdf8', description: 'Service discovery and health checking', defaultLatency: 3, defaultThroughput: 20000, defaultErrorRate: 0.02 },
  { id: 'grpc', name: 'gRPC Service', group: 'Distributed', icon: '📡', color: '#38bdf8', description: 'High-performance RPC framework over HTTP/2', defaultLatency: 5, defaultThroughput: 30000, defaultErrorRate: 0.1 },
  { id: 'graphql', name: 'GraphQL Server', group: 'Distributed', icon: '◈', color: '#38bdf8', description: 'Query language API — flexible data fetching', defaultLatency: 15, defaultThroughput: 8000, defaultErrorRate: 0.2 },

  // Reliability
  { id: 'circuit-breaker', name: 'Circuit Breaker', group: 'Reliability', icon: '⚡', color: '#4ade80', description: 'Fails fast when downstream is unhealthy', defaultLatency: 0.5, defaultThroughput: 50000, defaultErrorRate: 0 },
  { id: 'retry-logic', name: 'Retry Logic', group: 'Reliability', icon: '🔁', color: '#4ade80', description: 'Exponential backoff retry with jitter', defaultLatency: 2, defaultThroughput: 20000, defaultErrorRate: 0 },
  { id: 'bulkhead', name: 'Bulkhead', group: 'Reliability', icon: '🚧', color: '#4ade80', description: 'Isolates resource pools to prevent cascade failures', defaultLatency: 1, defaultThroughput: 30000, defaultErrorRate: 0 },
  { id: 'health-check', name: 'Health Check', group: 'Reliability', icon: '💓', color: '#4ade80', description: 'Liveness and readiness probes', defaultLatency: 0, defaultThroughput: 1000, defaultErrorRate: 0 },
  { id: 'fallback', name: 'Fallback Handler', group: 'Reliability', icon: '🛡️', color: '#4ade80', description: 'Returns cached or default response on failure', defaultLatency: 0.5, defaultThroughput: 50000, defaultErrorRate: 0 },

  // Security
  { id: 'waf', name: 'WAF', group: 'Security', icon: '🛡️', color: '#f43f5e', description: 'Web Application Firewall — blocks OWASP Top 10', defaultLatency: 2, defaultThroughput: 20000, defaultErrorRate: 0 },
  { id: 'oauth', name: 'OAuth 2.0 Server', group: 'Security', icon: '🔐', color: '#f43f5e', description: 'Authorization server — token issuance and validation', defaultLatency: 20, defaultThroughput: 5000, defaultErrorRate: 0.1 },
  { id: 'jwt', name: 'JWT Validator', group: 'Security', icon: '🎫', color: '#f43f5e', description: 'Stateless token validation middleware', defaultLatency: 1, defaultThroughput: 100000, defaultErrorRate: 0.01 },
  { id: 'vault', name: 'HashiCorp Vault', group: 'Security', icon: '🏦', color: '#f43f5e', description: 'Secrets management and encryption-as-a-service', defaultLatency: 5, defaultThroughput: 5000, defaultErrorRate: 0.01 },
  { id: 'ddos-protection', name: 'DDoS Protection', group: 'Security', icon: '🔰', color: '#f43f5e', description: 'Cloudflare / AWS Shield — absorbs volumetric attacks', defaultLatency: 1, defaultThroughput: 1000000, defaultErrorRate: 0 },

  // Observability
  { id: 'prometheus', name: 'Prometheus', group: 'Observability', icon: '🔥', color: '#a3e635', description: 'Time-series metrics collection and alerting', defaultLatency: 0, defaultThroughput: 0, defaultErrorRate: 0 },
  { id: 'grafana', name: 'Grafana', group: 'Observability', icon: '📊', color: '#a3e635', description: 'Metrics visualization and dashboards', defaultLatency: 0, defaultThroughput: 0, defaultErrorRate: 0 },
  { id: 'jaeger', name: 'Jaeger Tracing', group: 'Observability', icon: '🔍', color: '#a3e635', description: 'Distributed tracing — end-to-end request visibility', defaultLatency: 0, defaultThroughput: 0, defaultErrorRate: 0 },
  { id: 'elk', name: 'ELK Stack', group: 'Observability', icon: '📋', color: '#a3e635', description: 'Elasticsearch + Logstash + Kibana log pipeline', defaultLatency: 0, defaultThroughput: 0, defaultErrorRate: 0 },
  { id: 'datadog', name: 'Datadog', group: 'Observability', icon: '🐕', color: '#a3e635', description: 'Full-stack observability SaaS platform', defaultLatency: 0, defaultThroughput: 0, defaultErrorRate: 0 },
  { id: 'alertmanager', name: 'Alert Manager', group: 'Observability', icon: '🔔', color: '#a3e635', description: 'Routes and deduplicates alerts to notification channels', defaultLatency: 0, defaultThroughput: 0, defaultErrorRate: 0 },

  // Specialized
  { id: 'ml-model', name: 'ML Model Server', group: 'Specialized', icon: '🤖', color: '#c084fc', description: 'TensorFlow Serving / Triton — inference at scale', defaultLatency: 80, defaultThroughput: 1000, defaultErrorRate: 0.5 },
  { id: 'feature-store', name: 'Feature Store', group: 'Specialized', icon: '🗄️', color: '#c084fc', description: 'Centralized ML feature repository — Feast, Tecton', defaultLatency: 5, defaultThroughput: 10000, defaultErrorRate: 0.1 },
  { id: 'stream-processor', name: 'Stream Processor', group: 'Specialized', icon: '🌊', color: '#c084fc', description: 'Apache Flink / Spark Streaming — real-time ETL', defaultLatency: 20, defaultThroughput: 50000, defaultErrorRate: 0.2 },
  { id: 'scheduler', name: 'Job Scheduler', group: 'Specialized', icon: '⏰', color: '#c084fc', description: 'Cron-style job orchestration — Airflow, Temporal', defaultLatency: 0, defaultThroughput: 100, defaultErrorRate: 0.5 },
  { id: 'websocket-server', name: 'WebSocket Server', group: 'Specialized', icon: '🔌', color: '#c084fc', description: 'Persistent bidirectional connections — Socket.io', defaultLatency: 2, defaultThroughput: 10000, defaultErrorRate: 0.2 },
  { id: 'blockchain', name: 'Blockchain Node', group: 'Specialized', icon: '⛓️', color: '#c084fc', description: 'Distributed ledger for immutable transaction records', defaultLatency: 3000, defaultThroughput: 10, defaultErrorRate: 0.5 },
];

export interface ArchitecturePreset {
  id: string;
  name: string;
  description: string;
  category: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert';
  group?: 'use-case' | 'concept' | 'complexity' | 'alex-xu-v1' | 'alex-xu-v2';
  nodes: Omit<CanvasNode, 'metrics'>[];
  edges: CanvasEdge[];
}

export const PRESETS: ArchitecturePreset[] = [
  {
    id: 'preset-basic-web',
    name: 'Basic Web App',
    description: 'Browser → Load Balancer → App Server → PostgreSQL',
    category: 'Web',
    difficulty: 'Beginner',
    nodes: [
      { id: 'n1', defId: 'web-browser', x: 80, y: 200, label: 'Browser', status: 'idle', config: {} },
      { id: 'n2', defId: 'load-balancer', x: 280, y: 200, label: 'Load Balancer', status: 'idle', config: {} },
      { id: 'n3', defId: 'app-server', x: 480, y: 200, label: 'App Server', status: 'idle', config: {} },
      { id: 'n4', defId: 'postgres', x: 680, y: 200, label: 'PostgreSQL', status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'TCP', animated: false },
    ],
  },
  {
    id: 'preset-cached-api',
    name: 'Cached API Layer',
    description: 'API Gateway → Microservices → Redis Cache → PostgreSQL with Read Replicas',
    category: 'API',
    difficulty: 'Intermediate',
    nodes: [
      { id: 'n1', defId: 'web-browser', x: 60, y: 220, label: 'Browser', status: 'idle', config: {} },
      { id: 'n2', defId: 'cdn', x: 200, y: 100, label: 'CDN', status: 'idle', config: {} },
      { id: 'n3', defId: 'api-gateway', x: 320, y: 220, label: 'API Gateway', status: 'idle', config: {} },
      { id: 'n4', defId: 'microservice', x: 500, y: 140, label: 'User Service', status: 'idle', config: {} },
      { id: 'n5', defId: 'microservice', x: 500, y: 300, label: 'Order Service', status: 'idle', config: {} },
      { id: 'n6', defId: 'redis', x: 660, y: 140, label: 'Redis Cache', status: 'idle', config: {} },
      { id: 'n7', defId: 'postgres', x: 660, y: 300, label: 'PostgreSQL', status: 'idle', config: {} },
      { id: 'n8', defId: 'db-replica', x: 800, y: 300, label: 'Read Replica', status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n1', targetId: 'n3', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'HTTP', animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'HTTP', animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n6', protocol: 'TCP', animated: false },
      { id: 'e6', sourceId: 'n5', targetId: 'n7', protocol: 'TCP', animated: false },
      { id: 'e7', sourceId: 'n7', targetId: 'n8', protocol: 'TCP', animated: false },
    ],
  },
  {
    id: 'preset-event-driven',
    name: 'Event-Driven Microservices',
    description: 'API → Kafka → Multiple Consumers → Databases with Circuit Breakers',
    category: 'Microservices',
    difficulty: 'Advanced',
    nodes: [
      { id: 'n1', defId: 'api-gateway', x: 80, y: 240, label: 'API Gateway', status: 'idle', config: {} },
      { id: 'n2', defId: 'microservice', x: 260, y: 240, label: 'Order Service', status: 'idle', config: {} },
      { id: 'n3', defId: 'kafka', x: 440, y: 240, label: 'Kafka', status: 'idle', config: {} },
      { id: 'n4', defId: 'microservice', x: 620, y: 140, label: 'Inventory Svc', status: 'idle', config: {} },
      { id: 'n5', defId: 'microservice', x: 620, y: 340, label: 'Notification Svc', status: 'idle', config: {} },
      { id: 'n6', defId: 'circuit-breaker', x: 760, y: 140, label: 'Circuit Breaker', status: 'idle', config: {} },
      { id: 'n7', defId: 'postgres', x: 880, y: 140, label: 'Inventory DB', status: 'idle', config: {} },
      { id: 'n8', defId: 'redis', x: 880, y: 340, label: 'Notification Cache', status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'AMQP', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'AMQP', animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'AMQP', animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n6', protocol: 'HTTP', animated: false },
      { id: 'e6', sourceId: 'n6', targetId: 'n7', protocol: 'TCP', animated: false },
      { id: 'e7', sourceId: 'n5', targetId: 'n8', protocol: 'TCP', animated: false },
    ],
  },
  {
    id: 'preset-ml-pipeline',
    name: 'ML Inference Pipeline',
    description: 'Real-time ML serving with feature store, model server, and A/B traffic splitting',
    category: 'ML',
    difficulty: 'Expert',
    nodes: [
      { id: 'n1', defId: 'api-gateway', x: 60, y: 260, label: 'API Gateway', status: 'idle', config: {} },
      { id: 'n2', defId: 'load-balancer', x: 220, y: 260, label: 'Load Balancer', status: 'idle', config: {} },
      { id: 'n3', defId: 'feature-store', x: 380, y: 180, label: 'Feature Store', status: 'idle', config: {} },
      { id: 'n4', defId: 'redis', x: 380, y: 340, label: 'Feature Cache', status: 'idle', config: {} },
      { id: 'n5', defId: 'ml-model', x: 560, y: 180, label: 'Model v2 (prod)', status: 'idle', config: {} },
      { id: 'n6', defId: 'ml-model', x: 560, y: 340, label: 'Model v3 (canary)', status: 'idle', config: {} },
      { id: 'n7', defId: 'prometheus', x: 720, y: 260, label: 'Metrics', status: 'idle', config: {} },
      { id: 'n8', defId: 'kafka', x: 720, y: 140, label: 'Prediction Log', status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n5', protocol: 'gRPC', animated: false },
      { id: 'e3', sourceId: 'n2', targetId: 'n6', protocol: 'gRPC', animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'gRPC', animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n5', protocol: 'TCP', animated: false },
      { id: 'e6', sourceId: 'n5', targetId: 'n7', protocol: 'HTTP', animated: false },
      { id: 'e7', sourceId: 'n5', targetId: 'n8', protocol: 'AMQP', animated: false },
    ],
  },

  // ── BY USE CASE ──────────────────────────────────────────────────────────────

  {
    id: 'preset-netflix-streaming',
    name: 'Netflix-style Streaming',
    description: 'CDN + multiple origin servers + media encoding pipeline for large-scale video delivery',
    category: 'Streaming',
    difficulty: 'Advanced',
    group: 'use-case',
    nodes: [
      { id: 'n1', defId: 'mobile-app',      x: 60,  y: 120, label: 'Mobile App',       status: 'idle', config: {} },
      { id: 'n2', defId: 'web-browser',     x: 60,  y: 260, label: 'Web Browser',      status: 'idle', config: {} },
      { id: 'n3', defId: 'dns',             x: 200, y: 190, label: 'DNS',              status: 'idle', config: {} },
      { id: 'n4', defId: 'cdn',             x: 360, y: 120, label: 'CDN Edge PoP',     status: 'idle', config: {} },
      { id: 'n5', defId: 'load-balancer',   x: 360, y: 260, label: 'Load Balancer',    status: 'idle', config: {} },
      { id: 'n6', defId: 'app-server',      x: 540, y: 180, label: 'Origin Server A',  status: 'idle', config: {} },
      { id: 'n7', defId: 'app-server',      x: 540, y: 320, label: 'Origin Server B',  status: 'idle', config: {} },
      { id: 'n8', defId: 'stream-processor',x: 720, y: 120, label: 'Encoding Pipeline',status: 'idle', config: {} },
      { id: 'n9', defId: 'object-storage',  x: 720, y: 260, label: 'Video Storage',    status: 'idle', config: {} },
      { id: 'n10',defId: 'redis',           x: 880, y: 190, label: 'Session Cache',    status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n3', protocol: 'HTTP',      animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP',      animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'HTTP',      animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'HTTP',      animated: false },
      { id: 'e5', sourceId: 'n5', targetId: 'n6', protocol: 'HTTP',      animated: false },
      { id: 'e6', sourceId: 'n5', targetId: 'n7', protocol: 'HTTP',      animated: false },
      { id: 'e7', sourceId: 'n6', targetId: 'n8', protocol: 'TCP',       animated: false },
      { id: 'e8', sourceId: 'n8', targetId: 'n9', protocol: 'TCP',       animated: false },
      { id: 'e9', sourceId: 'n4', targetId: 'n9', protocol: 'HTTP',      animated: false },
      { id: 'e10',sourceId: 'n6', targetId: 'n10',protocol: 'TCP',       animated: false },
    ],
  },

  {
    id: 'preset-twitter-feed',
    name: 'Twitter-style Social Feed',
    description: 'Fanout-on-write vs fanout-on-read with timeline caching and pub/sub notifications',
    category: 'Social',
    difficulty: 'Advanced',
    group: 'use-case',
    nodes: [
      { id: 'n1', defId: 'mobile-app',    x: 60,  y: 200, label: 'Mobile Client',   status: 'idle', config: {} },
      { id: 'n2', defId: 'api-gateway',   x: 220, y: 200, label: 'API Gateway',     status: 'idle', config: {} },
      { id: 'n3', defId: 'microservice',  x: 400, y: 120, label: 'Tweet Service',   status: 'idle', config: {} },
      { id: 'n4', defId: 'microservice',  x: 400, y: 280, label: 'Feed Service',    status: 'idle', config: {} },
      { id: 'n5', defId: 'kafka',         x: 580, y: 200, label: 'Kafka (Fanout)',  status: 'idle', config: {} },
      { id: 'n6', defId: 'worker',        x: 740, y: 120, label: 'Fanout Worker',   status: 'idle', config: {} },
      { id: 'n7', defId: 'redis',         x: 740, y: 280, label: 'Timeline Cache',  status: 'idle', config: {} },
      { id: 'n8', defId: 'cassandra',     x: 900, y: 200, label: 'Tweet Store',     status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP',  animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP',  animated: false },
      { id: 'e3', sourceId: 'n2', targetId: 'n4', protocol: 'HTTP',  animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'AMQP',  animated: false },
      { id: 'e5', sourceId: 'n5', targetId: 'n6', protocol: 'AMQP',  animated: false },
      { id: 'e6', sourceId: 'n6', targetId: 'n7', protocol: 'TCP',   animated: false },
      { id: 'e7', sourceId: 'n4', targetId: 'n7', protocol: 'TCP',   animated: false },
      { id: 'e8', sourceId: 'n3', targetId: 'n8', protocol: 'TCP',   animated: false },
    ],
  },

  {
    id: 'preset-uber-ride-matching',
    name: 'Uber-style Ride Matching',
    description: 'Geospatial DB, WebSocket connections, surge pricing service for real-time matching',
    category: 'Real-time',
    difficulty: 'Expert',
    group: 'use-case',
    nodes: [
      { id: 'n1', defId: 'mobile-app',       x: 60,  y: 200, label: 'Rider App',        status: 'idle', config: {} },
      { id: 'n2', defId: 'mobile-app',       x: 60,  y: 340, label: 'Driver App',       status: 'idle', config: {} },
      { id: 'n3', defId: 'api-gateway',      x: 220, y: 270, label: 'API Gateway',      status: 'idle', config: {} },
      { id: 'n4', defId: 'websocket-server', x: 400, y: 200, label: 'WebSocket Server', status: 'idle', config: {} },
      { id: 'n5', defId: 'microservice',     x: 400, y: 340, label: 'Matching Service', status: 'idle', config: {} },
      { id: 'n6', defId: 'redis',            x: 580, y: 200, label: 'Location Cache',   status: 'idle', config: {} },
      { id: 'n7', defId: 'microservice',     x: 580, y: 340, label: 'Surge Pricing Svc',status: 'idle', config: {} },
      { id: 'n8', defId: 'postgres',         x: 760, y: 270, label: 'Geospatial DB',    status: 'idle', config: {} },
      { id: 'n9', defId: 'kafka',            x: 760, y: 140, label: 'Trip Events',      status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n3', protocol: 'HTTP',      animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP',      animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'WebSocket', animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'HTTP',      animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n6', protocol: 'TCP',       animated: false },
      { id: 'e6', sourceId: 'n5', targetId: 'n6', protocol: 'TCP',       animated: false },
      { id: 'e7', sourceId: 'n5', targetId: 'n7', protocol: 'HTTP',      animated: false },
      { id: 'e8', sourceId: 'n5', targetId: 'n8', protocol: 'TCP',       animated: false },
      { id: 'e9', sourceId: 'n5', targetId: 'n9', protocol: 'AMQP',      animated: false },
    ],
  },

  {
    id: 'preset-ecommerce-checkout',
    name: 'E-commerce Checkout',
    description: 'Payment gateway, inventory lock, order queue, and fraud detection pipeline',
    category: 'E-commerce',
    difficulty: 'Advanced',
    group: 'use-case',
    nodes: [
      { id: 'n1', defId: 'web-browser',   x: 60,  y: 220, label: 'Browser',          status: 'idle', config: {} },
      { id: 'n2', defId: 'api-gateway',   x: 220, y: 220, label: 'API Gateway',      status: 'idle', config: {} },
      { id: 'n3', defId: 'microservice',  x: 400, y: 140, label: 'Order Service',    status: 'idle', config: {} },
      { id: 'n4', defId: 'microservice',  x: 400, y: 300, label: 'Fraud Detection',  status: 'idle', config: {} },
      { id: 'n5', defId: 'microservice',  x: 580, y: 140, label: 'Payment Gateway',  status: 'idle', config: {} },
      { id: 'n6', defId: 'redis',         x: 580, y: 300, label: 'Inventory Lock',   status: 'idle', config: {} },
      { id: 'n7', defId: 'sqs',           x: 760, y: 140, label: 'Order Queue',      status: 'idle', config: {} },
      { id: 'n8', defId: 'postgres',      x: 760, y: 300, label: 'Orders DB',        status: 'idle', config: {} },
      { id: 'n9', defId: 'worker',        x: 920, y: 140, label: 'Fulfillment Worker',status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP',  animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP',  animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'HTTP',  animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'HTTP',  animated: false },
      { id: 'e5', sourceId: 'n3', targetId: 'n6', protocol: 'TCP',   animated: false },
      { id: 'e6', sourceId: 'n5', targetId: 'n7', protocol: 'AMQP',  animated: false },
      { id: 'e7', sourceId: 'n5', targetId: 'n8', protocol: 'TCP',   animated: false },
      { id: 'e8', sourceId: 'n7', targetId: 'n9', protocol: 'AMQP',  animated: false },
    ],
  },

  {
    id: 'preset-chat-app',
    name: 'Chat Application',
    description: 'WebSocket servers, message broker, and presence service for real-time messaging',
    category: 'Messaging',
    difficulty: 'Intermediate',
    group: 'use-case',
    nodes: [
      { id: 'n1', defId: 'web-browser',      x: 60,  y: 200, label: 'Browser',         status: 'idle', config: {} },
      { id: 'n2', defId: 'mobile-app',       x: 60,  y: 340, label: 'Mobile App',      status: 'idle', config: {} },
      { id: 'n3', defId: 'load-balancer',    x: 220, y: 270, label: 'Load Balancer',   status: 'idle', config: {} },
      { id: 'n4', defId: 'websocket-server', x: 400, y: 200, label: 'WS Server A',     status: 'idle', config: {} },
      { id: 'n5', defId: 'websocket-server', x: 400, y: 340, label: 'WS Server B',     status: 'idle', config: {} },
      { id: 'n6', defId: 'redis',            x: 580, y: 200, label: 'Presence Service',status: 'idle', config: {} },
      { id: 'n7', defId: 'rabbitmq',         x: 580, y: 340, label: 'Message Broker',  status: 'idle', config: {} },
      { id: 'n8', defId: 'cassandra',        x: 760, y: 270, label: 'Message Store',   status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n3', protocol: 'WebSocket', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'WebSocket', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'WebSocket', animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'WebSocket', animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n6', protocol: 'TCP',       animated: false },
      { id: 'e6', sourceId: 'n4', targetId: 'n7', protocol: 'AMQP',      animated: false },
      { id: 'e7', sourceId: 'n5', targetId: 'n7', protocol: 'AMQP',      animated: false },
      { id: 'e8', sourceId: 'n7', targetId: 'n8', protocol: 'TCP',       animated: false },
    ],
  },

  // ── BY CONCEPT / EDUCATIONAL ─────────────────────────────────────────────────

  {
    id: 'preset-cache-stampede',
    name: 'Cache Stampede',
    description: 'Shows what happens when cache expires under heavy load — thundering herd problem',
    category: 'Caching',
    difficulty: 'Intermediate',
    group: 'concept',
    nodes: [
      { id: 'n1', defId: 'web-browser',   x: 60,  y: 120, label: 'Client A',       status: 'idle', config: {} },
      { id: 'n2', defId: 'web-browser',   x: 60,  y: 220, label: 'Client B',       status: 'idle', config: {} },
      { id: 'n3', defId: 'web-browser',   x: 60,  y: 320, label: 'Client C',       status: 'idle', config: {} },
      { id: 'n4', defId: 'load-balancer', x: 240, y: 220, label: 'Load Balancer',  status: 'idle', config: {} },
      { id: 'n5', defId: 'redis',         x: 420, y: 220, label: 'Redis (expired)',status: 'idle', config: {} },
      { id: 'n6', defId: 'app-server',    x: 600, y: 140, label: 'App Server A',   status: 'idle', config: {} },
      { id: 'n7', defId: 'app-server',    x: 600, y: 300, label: 'App Server B',   status: 'idle', config: {} },
      { id: 'n8', defId: 'postgres',      x: 800, y: 220, label: 'Primary DB',     status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n4', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n4', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'HTTP', animated: false },
      { id: 'e4', sourceId: 'n4', targetId: 'n5', protocol: 'TCP',  animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n6', protocol: 'HTTP', animated: false },
      { id: 'e6', sourceId: 'n4', targetId: 'n7', protocol: 'HTTP', animated: false },
      { id: 'e7', sourceId: 'n6', targetId: 'n8', protocol: 'TCP',  animated: false },
      { id: 'e8', sourceId: 'n7', targetId: 'n8', protocol: 'TCP',  animated: false },
    ],
  },

  {
    id: 'preset-circuit-breaker',
    name: 'Circuit Breaker in Action',
    description: 'Upstream failure cascading vs. isolated failure — circuit breaker prevents cascade',
    category: 'Reliability',
    difficulty: 'Intermediate',
    group: 'concept',
    nodes: [
      { id: 'n1', defId: 'api-gateway',     x: 60,  y: 220, label: 'API Gateway',     status: 'idle', config: {} },
      { id: 'n2', defId: 'microservice',    x: 240, y: 140, label: 'Service A',        status: 'idle', config: {} },
      { id: 'n3', defId: 'microservice',    x: 240, y: 300, label: 'Service B',        status: 'idle', config: {} },
      { id: 'n4', defId: 'circuit-breaker', x: 440, y: 140, label: 'Circuit Breaker',  status: 'idle', config: {} },
      { id: 'n5', defId: 'fallback',        x: 440, y: 300, label: 'Fallback Handler', status: 'idle', config: {} },
      { id: 'n6', defId: 'microservice',    x: 640, y: 140, label: 'Downstream Svc',   status: 'idle', config: {} },
      { id: 'n7', defId: 'health-check',    x: 640, y: 300, label: 'Health Check',     status: 'idle', config: {} },
      { id: 'n8', defId: 'prometheus',      x: 820, y: 220, label: 'Metrics',          status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n1', targetId: 'n3', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n2', targetId: 'n4', protocol: 'HTTP', animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'HTTP', animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n6', protocol: 'HTTP', animated: false },
      { id: 'e6', sourceId: 'n5', targetId: 'n7', protocol: 'HTTP', animated: false },
      { id: 'e7', sourceId: 'n6', targetId: 'n8', protocol: 'HTTP', animated: false },
      { id: 'e8', sourceId: 'n7', targetId: 'n8', protocol: 'HTTP', animated: false },
    ],
  },

  {
    id: 'preset-read-replica',
    name: 'Read Replica Scaling',
    description: 'Primary DB + replicas with read/write splitting for horizontal read scalability',
    category: 'Database',
    difficulty: 'Intermediate',
    group: 'concept',
    nodes: [
      { id: 'n1', defId: 'web-browser',  x: 60,  y: 220, label: 'Browser',       status: 'idle', config: {} },
      { id: 'n2', defId: 'api-gateway',  x: 220, y: 220, label: 'API Gateway',   status: 'idle', config: {} },
      { id: 'n3', defId: 'app-server',   x: 400, y: 140, label: 'Write Service', status: 'idle', config: {} },
      { id: 'n4', defId: 'app-server',   x: 400, y: 300, label: 'Read Service',  status: 'idle', config: {} },
      { id: 'n5', defId: 'postgres',     x: 600, y: 140, label: 'Primary DB',    status: 'idle', config: {} },
      { id: 'n6', defId: 'db-replica',   x: 600, y: 260, label: 'Read Replica 1',status: 'idle', config: {} },
      { id: 'n7', defId: 'db-replica',   x: 600, y: 360, label: 'Read Replica 2',status: 'idle', config: {} },
      { id: 'n8', defId: 'redis',        x: 800, y: 220, label: 'Query Cache',   status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n2', targetId: 'n4', protocol: 'HTTP', animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'TCP',  animated: false },
      { id: 'e5', sourceId: 'n5', targetId: 'n6', protocol: 'TCP',  animated: false },
      { id: 'e6', sourceId: 'n5', targetId: 'n7', protocol: 'TCP',  animated: false },
      { id: 'e7', sourceId: 'n4', targetId: 'n6', protocol: 'TCP',  animated: false },
      { id: 'e8', sourceId: 'n4', targetId: 'n8', protocol: 'TCP',  animated: false },
    ],
  },

  {
    id: 'preset-event-driven-arch',
    name: 'Event-Driven Architecture',
    description: 'Producer → Kafka → multiple consumers with dead-letter queue for failed events',
    category: 'Messaging',
    difficulty: 'Advanced',
    group: 'concept',
    nodes: [
      { id: 'n1', defId: 'api-client',    x: 60,  y: 220, label: 'Event Producer',  status: 'idle', config: {} },
      { id: 'n2', defId: 'kafka',         x: 240, y: 220, label: 'Kafka Broker',    status: 'idle', config: {} },
      { id: 'n3', defId: 'worker',        x: 440, y: 120, label: 'Consumer A',      status: 'idle', config: {} },
      { id: 'n4', defId: 'worker',        x: 440, y: 220, label: 'Consumer B',      status: 'idle', config: {} },
      { id: 'n5', defId: 'worker',        x: 440, y: 320, label: 'Consumer C',      status: 'idle', config: {} },
      { id: 'n6', defId: 'dead-letter',   x: 620, y: 120, label: 'Dead Letter Q',   status: 'idle', config: {} },
      { id: 'n7', defId: 'mongodb',       x: 620, y: 220, label: 'Event Store',     status: 'idle', config: {} },
      { id: 'n8', defId: 'elasticsearch', x: 620, y: 320, label: 'Search Index',    status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'AMQP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'AMQP', animated: false },
      { id: 'e3', sourceId: 'n2', targetId: 'n4', protocol: 'AMQP', animated: false },
      { id: 'e4', sourceId: 'n2', targetId: 'n5', protocol: 'AMQP', animated: false },
      { id: 'e5', sourceId: 'n3', targetId: 'n6', protocol: 'AMQP', animated: false },
      { id: 'e6', sourceId: 'n4', targetId: 'n7', protocol: 'TCP',  animated: false },
      { id: 'e7', sourceId: 'n5', targetId: 'n8', protocol: 'HTTP', animated: false },
    ],
  },

  {
    id: 'preset-cqrs',
    name: 'CQRS Pattern',
    description: 'Separate read/write models with event store — Command Query Responsibility Segregation',
    category: 'Patterns',
    difficulty: 'Advanced',
    group: 'concept',
    nodes: [
      { id: 'n1', defId: 'web-browser',   x: 60,  y: 220, label: 'Client',          status: 'idle', config: {} },
      { id: 'n2', defId: 'api-gateway',   x: 220, y: 220, label: 'API Gateway',     status: 'idle', config: {} },
      { id: 'n3', defId: 'microservice',  x: 400, y: 140, label: 'Command Handler', status: 'idle', config: {} },
      { id: 'n4', defId: 'microservice',  x: 400, y: 300, label: 'Query Handler',   status: 'idle', config: {} },
      { id: 'n5', defId: 'event-bus',     x: 580, y: 140, label: 'Event Store',     status: 'idle', config: {} },
      { id: 'n6', defId: 'postgres',      x: 580, y: 300, label: 'Read Model DB',   status: 'idle', config: {} },
      { id: 'n7', defId: 'worker',        x: 760, y: 140, label: 'Projection Worker',status: 'idle', config: {} },
      { id: 'n8', defId: 'redis',         x: 760, y: 300, label: 'Read Cache',      status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP',  animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP',  animated: false },
      { id: 'e3', sourceId: 'n2', targetId: 'n4', protocol: 'HTTP',  animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'AMQP',  animated: false },
      { id: 'e5', sourceId: 'n5', targetId: 'n7', protocol: 'AMQP',  animated: false },
      { id: 'e6', sourceId: 'n7', targetId: 'n6', protocol: 'TCP',   animated: false },
      { id: 'e7', sourceId: 'n4', targetId: 'n6', protocol: 'TCP',   animated: false },
      { id: 'e8', sourceId: 'n4', targetId: 'n8', protocol: 'TCP',   animated: false },
    ],
  },

  // ── BY COMPLEXITY ─────────────────────────────────────────────────────────────

  {
    id: 'preset-starter',
    name: 'Starter',
    description: 'Single server + DB + load balancer — the simplest production-ready setup',
    category: 'Starter',
    difficulty: 'Beginner',
    group: 'complexity',
    nodes: [
      { id: 'n1', defId: 'web-browser',   x: 80,  y: 200, label: 'Browser',       status: 'idle', config: {} },
      { id: 'n2', defId: 'load-balancer', x: 280, y: 200, label: 'Load Balancer', status: 'idle', config: {} },
      { id: 'n3', defId: 'web-server',    x: 480, y: 200, label: 'Web Server',    status: 'idle', config: {} },
      { id: 'n4', defId: 'postgres',      x: 680, y: 200, label: 'PostgreSQL',    status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'TCP',  animated: false },
    ],
  },

  // ── ALEX XU — VOLUME 1 ────────────────────────────────────────────────────────

  {
    id: 'xu-v1-scale-from-zero',
    name: 'Scale from Zero',
    description: 'Single server → LB → App Servers → Cache → DB with Read Replica. Foundation of horizontal scaling.',
    category: 'Scaling',
    difficulty: 'Beginner',
    group: 'alex-xu-v1',
    nodes: [
      { id: 'n1', defId: 'web-browser',   x: 60,  y: 220, label: 'Client',          status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'dns',           x: 200, y: 220, label: 'DNS',             status: 'idle', config: {} },
      { id: 'n3', defId: 'load-balancer', x: 360, y: 220, label: 'Load Balancer',   status: 'idle', config: { instances: 2, serviceRatePerInstance: 5000 } },
      { id: 'n4', defId: 'web-server',    x: 540, y: 140, label: 'Web Server A',    status: 'idle', config: { instances: 2, serviceRatePerInstance: 2000 } },
      { id: 'n5', defId: 'web-server',    x: 540, y: 300, label: 'Web Server B',    status: 'idle', config: { instances: 2, serviceRatePerInstance: 2000 } },
      { id: 'n6', defId: 'redis',         x: 720, y: 140, label: 'Redis Cache',     status: 'idle', config: { hitRate: 0.8, cacheSizeGB: 8 } },
      { id: 'n7', defId: 'mysql',         x: 720, y: 300, label: 'MySQL Primary',   status: 'idle', config: { instances: 1, serviceRatePerInstance: 800 } },
      { id: 'n8', defId: 'db-replica',    x: 900, y: 300, label: 'Read Replica',    status: 'idle', config: { instances: 1, serviceRatePerInstance: 1200 } },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'HTTP', animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'HTTP', animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n6', protocol: 'TCP',  animated: false },
      { id: 'e6', sourceId: 'n5', targetId: 'n6', protocol: 'TCP',  animated: false },
      { id: 'e7', sourceId: 'n4', targetId: 'n7', protocol: 'TCP',  animated: false },
      { id: 'e8', sourceId: 'n7', targetId: 'n8', protocol: 'TCP',  animated: false },
    ],
  },

  {
    id: 'xu-v1-rate-limiter',
    name: 'Rate Limiter',
    description: 'Token-bucket rate limiter in front of API servers — throttles excess traffic, protects downstream.',
    category: 'Traffic Control',
    difficulty: 'Intermediate',
    group: 'alex-xu-v1',
    nodes: [
      { id: 'n1', defId: 'web-browser',   x: 60,  y: 220, label: 'Client',          status: 'idle', config: { rpsOverride: 1500 } },
      { id: 'n2', defId: 'api-gateway',   x: 220, y: 220, label: 'API Gateway',     status: 'idle', config: { instances: 2, serviceRatePerInstance: 3000 } },
      { id: 'n3', defId: 'rate-limiter',  x: 400, y: 220, label: 'Rate Limiter',    status: 'idle', config: { rateLimit: 1000, windowSec: 1, algorithm: 'token-bucket' } },
      { id: 'n4', defId: 'app-server',    x: 580, y: 140, label: 'App Server A',    status: 'idle', config: { instances: 2, serviceRatePerInstance: 1500 } },
      { id: 'n5', defId: 'app-server',    x: 580, y: 300, label: 'App Server B',    status: 'idle', config: { instances: 2, serviceRatePerInstance: 1500 } },
      { id: 'n6', defId: 'redis',         x: 760, y: 220, label: 'Rate Limit Store',status: 'idle', config: { hitRate: 0.99, cacheSizeGB: 2 } },
      { id: 'n7', defId: 'mysql',         x: 940, y: 220, label: 'App DB',          status: 'idle', config: { instances: 1, serviceRatePerInstance: 1000 } },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'HTTP', animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'HTTP', animated: false },
      { id: 'e5', sourceId: 'n3', targetId: 'n6', protocol: 'TCP',  animated: false },
      { id: 'e6', sourceId: 'n4', targetId: 'n7', protocol: 'TCP',  animated: false },
      { id: 'e7', sourceId: 'n5', targetId: 'n7', protocol: 'TCP',  animated: false },
    ],
  },

  {
    id: 'xu-v1-consistent-hashing',
    name: 'Consistent Hashing',
    description: 'Client → LB → App → Consistent-hash ring of DB shards. Minimal key remapping on node add/remove.',
    category: 'Distributed',
    difficulty: 'Intermediate',
    group: 'alex-xu-v1',
    nodes: [
      { id: 'n1', defId: 'api-client',    x: 60,  y: 220, label: 'Client',          status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'load-balancer', x: 220, y: 220, label: 'Load Balancer',   status: 'idle', config: { instances: 2, serviceRatePerInstance: 5000 } },
      { id: 'n3', defId: 'app-server',    x: 400, y: 220, label: 'App Server',      status: 'idle', config: { instances: 3, serviceRatePerInstance: 2000 } },
      { id: 'n4', defId: 'db-shard',      x: 600, y: 100, label: 'Shard A (0–90°)', status: 'idle', config: { instances: 1, serviceRatePerInstance: 1500, shardKey: 'userId', vnodes: 150 } },
      { id: 'n5', defId: 'db-shard',      x: 600, y: 220, label: 'Shard B (90–180°)',status: 'idle', config: { instances: 1, serviceRatePerInstance: 1500, shardKey: 'userId', vnodes: 150 } },
      { id: 'n6', defId: 'db-shard',      x: 600, y: 340, label: 'Shard C (180–270°)',status: 'idle', config: { instances: 1, serviceRatePerInstance: 1500, shardKey: 'userId', vnodes: 150 } },
      { id: 'n7', defId: 'zookeeper',     x: 800, y: 220, label: 'ZooKeeper',       status: 'idle', config: { instances: 3 } },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP',  animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP',  animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'TCP',   animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'TCP',   animated: false },
      { id: 'e5', sourceId: 'n3', targetId: 'n6', protocol: 'TCP',   animated: false },
      { id: 'e6', sourceId: 'n3', targetId: 'n7', protocol: 'TCP',   animated: false },
    ],
  },

  {
    id: 'xu-v1-key-value-store',
    name: 'Key-Value Store',
    description: 'Distributed KV store: client → app → Redis cluster → persistent DB. High read/write throughput.',
    category: 'Storage',
    difficulty: 'Intermediate',
    group: 'alex-xu-v1',
    nodes: [
      { id: 'n1', defId: 'api-client',         x: 60,  y: 220, label: 'Client',              status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'load-balancer',       x: 220, y: 220, label: 'Load Balancer',       status: 'idle', config: { instances: 2, serviceRatePerInstance: 5000 } },
      { id: 'n3', defId: 'app-server',          x: 400, y: 220, label: 'KV Service',          status: 'idle', config: { instances: 3, serviceRatePerInstance: 3000 } },
      { id: 'n4', defId: 'distributed-cache',   x: 580, y: 140, label: 'Redis Cluster',       status: 'idle', config: { hitRate: 0.85, cacheSizeGB: 32, instances: 3 } },
      { id: 'n5', defId: 'cassandra',           x: 580, y: 300, label: 'Cassandra (persist)', status: 'idle', config: { instances: 3, serviceRatePerInstance: 2000 } },
      { id: 'n6', defId: 'zookeeper',           x: 780, y: 220, label: 'Coordinator',         status: 'idle', config: { instances: 3 } },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'TCP',  animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'TCP',  animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n5', protocol: 'TCP',  animated: false },
      { id: 'e6', sourceId: 'n3', targetId: 'n6', protocol: 'TCP',  animated: false },
    ],
  },

  {
    id: 'xu-v1-unique-id-generator',
    name: 'Unique ID Generator',
    description: 'Snowflake-style distributed ID generation: low latency, high availability, no single point of failure.',
    category: 'Infrastructure',
    difficulty: 'Intermediate',
    group: 'alex-xu-v1',
    nodes: [
      { id: 'n1', defId: 'api-client',    x: 60,  y: 220, label: 'Client',          status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'load-balancer', x: 220, y: 220, label: 'Load Balancer',   status: 'idle', config: { instances: 2, serviceRatePerInstance: 10000 } },
      { id: 'n3', defId: 'microservice',  x: 400, y: 140, label: 'ID Gen Node 1',   status: 'idle', config: { instances: 1, serviceRatePerInstance: 50000, datacenterBits: 5, machineBits: 5 } },
      { id: 'n4', defId: 'microservice',  x: 400, y: 300, label: 'ID Gen Node 2',   status: 'idle', config: { instances: 1, serviceRatePerInstance: 50000, datacenterBits: 5, machineBits: 5 } },
      { id: 'n5', defId: 'zookeeper',     x: 600, y: 220, label: 'ZooKeeper',       status: 'idle', config: { instances: 3 } },
      { id: 'n6', defId: 'redis',         x: 800, y: 220, label: 'Sequence Cache',  status: 'idle', config: { hitRate: 0.99, cacheSizeGB: 1 } },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n2', targetId: 'n4', protocol: 'HTTP', animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'TCP',  animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n5', protocol: 'TCP',  animated: false },
      { id: 'e6', sourceId: 'n3', targetId: 'n6', protocol: 'TCP',  animated: false },
    ],
  },

  {
    id: 'xu-v1-url-shortener',
    name: 'URL Shortener',
    description: 'tinyURL-style: CDN + cache-aside for reads, DB for writes. Cache hit rate > 80% expected.',
    category: 'Web',
    difficulty: 'Beginner',
    group: 'alex-xu-v1',
    nodes: [
      { id: 'n1', defId: 'web-browser',   x: 60,  y: 220, label: 'Client',          status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'cdn',           x: 220, y: 140, label: 'CDN',             status: 'idle', config: { hitRate: 0.6, cacheSizeGB: 50 } },
      { id: 'n3', defId: 'api-gateway',   x: 220, y: 300, label: 'API Gateway',     status: 'idle', config: { instances: 2, serviceRatePerInstance: 5000 } },
      { id: 'n4', defId: 'app-server',    x: 420, y: 220, label: 'Redirect Service',status: 'idle', config: { instances: 3, serviceRatePerInstance: 2000 } },
      { id: 'n5', defId: 'redis',         x: 620, y: 140, label: 'URL Cache',       status: 'idle', config: { hitRate: 0.85, cacheSizeGB: 8 } },
      { id: 'n6', defId: 'mysql',         x: 620, y: 300, label: 'URL DB',          status: 'idle', config: { instances: 1, serviceRatePerInstance: 800 } },
      { id: 'n7', defId: 'kafka',         x: 820, y: 220, label: 'Analytics Queue', status: 'idle', config: { partitions: 8, replicationFactor: 3 } },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n1', targetId: 'n3', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'HTTP', animated: false },
      { id: 'e4', sourceId: 'n4', targetId: 'n5', protocol: 'TCP',  animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n6', protocol: 'TCP',  animated: false },
      { id: 'e6', sourceId: 'n4', targetId: 'n7', protocol: 'AMQP', animated: false },
    ],
  },

  {
    id: 'xu-v1-web-crawler',
    name: 'Web Crawler',
    description: 'Async crawl pipeline: Scheduler → URL Frontier → Workers → DNS → Content Store. Queue-depth driven.',
    category: 'Data Pipeline',
    difficulty: 'Advanced',
    group: 'alex-xu-v1',
    nodes: [
      { id: 'n1', defId: 'scheduler',     x: 60,  y: 220, label: 'Crawl Scheduler', status: 'idle', config: { rpsOverride: 200, jobIntervalSec: 60 } },
      { id: 'n2', defId: 'sqs',           x: 240, y: 220, label: 'URL Frontier',    status: 'idle', config: { maxQueueDepth: 10000000 } },
      { id: 'n3', defId: 'worker',        x: 420, y: 140, label: 'Crawler Worker A',status: 'idle', config: { instances: 5, serviceRatePerInstance: 200 } },
      { id: 'n4', defId: 'worker',        x: 420, y: 300, label: 'Crawler Worker B',status: 'idle', config: { instances: 5, serviceRatePerInstance: 200 } },
      { id: 'n5', defId: 'dns',           x: 600, y: 220, label: 'DNS Resolver',    status: 'idle', config: {} },
      { id: 'n6', defId: 'redis',         x: 780, y: 140, label: 'URL Seen Filter', status: 'idle', config: { hitRate: 0.7, cacheSizeGB: 4 } },
      { id: 'n7', defId: 'object-storage',x: 780, y: 300, label: 'Content Store',   status: 'idle', config: {} },
      { id: 'n8', defId: 'elasticsearch', x: 960, y: 220, label: 'Search Index',    status: 'idle', config: { instances: 3, serviceRatePerInstance: 500 } },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'AMQP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'AMQP', animated: false },
      { id: 'e3', sourceId: 'n2', targetId: 'n4', protocol: 'AMQP', animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'HTTP', animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n5', protocol: 'HTTP', animated: false },
      { id: 'e6', sourceId: 'n3', targetId: 'n6', protocol: 'TCP',  animated: false },
      { id: 'e7', sourceId: 'n3', targetId: 'n7', protocol: 'HTTP', animated: false },
      { id: 'e8', sourceId: 'n4', targetId: 'n7', protocol: 'HTTP', animated: false },
      { id: 'e9', sourceId: 'n7', targetId: 'n8', protocol: 'HTTP', animated: false },
    ],
  },

  {
    id: 'xu-v1-notification-system',
    name: 'Notification System',
    description: 'High fan-out: App → Kafka → Workers → Push/Email/SMS. One request fans out to millions of devices.',
    category: 'Messaging',
    difficulty: 'Advanced',
    group: 'alex-xu-v1',
    nodes: [
      { id: 'n1', defId: 'api-client',    x: 60,  y: 220, label: 'Notification API',status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'app-server',    x: 220, y: 220, label: 'Notification Svc',status: 'idle', config: { instances: 3, serviceRatePerInstance: 2000 } },
      { id: 'n3', defId: 'kafka',         x: 400, y: 220, label: 'Kafka (fan-out)', status: 'idle', config: { partitions: 16, replicationFactor: 3 } },
      { id: 'n4', defId: 'worker',        x: 580, y: 100, label: 'Push Worker',     status: 'idle', config: { instances: 10, serviceRatePerInstance: 500 } },
      { id: 'n5', defId: 'worker',        x: 580, y: 220, label: 'Email Worker',    status: 'idle', config: { instances: 5, serviceRatePerInstance: 200 } },
      { id: 'n6', defId: 'worker',        x: 580, y: 340, label: 'SMS Worker',      status: 'idle', config: { instances: 3, serviceRatePerInstance: 100 } },
      { id: 'n7', defId: 'redis',         x: 780, y: 220, label: 'Device Token Cache',status: 'idle', config: { hitRate: 0.9, cacheSizeGB: 16 } },
      { id: 'n8', defId: 'cassandra',     x: 960, y: 220, label: 'Notification DB', status: 'idle', config: { instances: 3, serviceRatePerInstance: 3000 } },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'AMQP', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'AMQP', animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'AMQP', animated: false },
      { id: 'e5', sourceId: 'n3', targetId: 'n6', protocol: 'AMQP', animated: false },
      { id: 'e6', sourceId: 'n4', targetId: 'n7', protocol: 'TCP',  animated: false },
      { id: 'e7', sourceId: 'n5', targetId: 'n7', protocol: 'TCP',  animated: false },
      { id: 'e8', sourceId: 'n4', targetId: 'n8', protocol: 'TCP',  animated: false },
    ],
  },

  {
    id: 'xu-v1-news-feed',
    name: 'News Feed System',
    description: 'Fan-out-on-write: Post → Kafka → Fanout Workers → Timeline Cache. Cache serves reads at scale.',
    category: 'Social',
    difficulty: 'Advanced',
    group: 'alex-xu-v1',
    nodes: [
      { id: 'n1', defId: 'mobile-app',    x: 60,  y: 220, label: 'Mobile Client',  status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'api-gateway',   x: 220, y: 220, label: 'API Gateway',    status: 'idle', config: { instances: 2, serviceRatePerInstance: 5000 } },
      { id: 'n3', defId: 'load-balancer', x: 380, y: 220, label: 'Load Balancer',  status: 'idle', config: { instances: 2, serviceRatePerInstance: 5000 } },
      { id: 'n4', defId: 'microservice',  x: 560, y: 140, label: 'Post Service',   status: 'idle', config: { instances: 3, serviceRatePerInstance: 1500 } },
      { id: 'n5', defId: 'microservice',  x: 560, y: 300, label: 'Feed Service',   status: 'idle', config: { instances: 3, serviceRatePerInstance: 2000 } },
      { id: 'n6', defId: 'kafka',         x: 740, y: 140, label: 'Fanout Queue',   status: 'idle', config: { partitions: 16, replicationFactor: 3 } },
      { id: 'n7', defId: 'worker',        x: 920, y: 140, label: 'Fanout Worker',  status: 'idle', config: { instances: 10, serviceRatePerInstance: 500 } },
      { id: 'n8', defId: 'redis',         x: 740, y: 300, label: 'Timeline Cache', status: 'idle', config: { hitRate: 0.9, cacheSizeGB: 64 } },
      { id: 'n9', defId: 'cassandra',     x: 920, y: 300, label: 'Post DB',        status: 'idle', config: { instances: 3, serviceRatePerInstance: 2000 } },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'HTTP', animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'HTTP', animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n6', protocol: 'AMQP', animated: false },
      { id: 'e6', sourceId: 'n6', targetId: 'n7', protocol: 'AMQP', animated: false },
      { id: 'e7', sourceId: 'n7', targetId: 'n8', protocol: 'TCP',  animated: false },
      { id: 'e8', sourceId: 'n5', targetId: 'n8', protocol: 'TCP',  animated: false },
      { id: 'e9', sourceId: 'n4', targetId: 'n9', protocol: 'TCP',  animated: false },
    ],
  },

  {
    id: 'xu-v1-chat-system',
    name: 'Chat System',
    description: 'WebSocket gateway + presence service + message queue + Cassandra for 1:1 and group messaging.',
    category: 'Real-time',
    difficulty: 'Advanced',
    group: 'alex-xu-v1',
    nodes: [
      { id: 'n1', defId: 'mobile-app',       x: 60,  y: 200, label: 'Mobile Client',   status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'load-balancer',    x: 220, y: 200, label: 'Load Balancer',   status: 'idle', config: { instances: 2, serviceRatePerInstance: 5000 } },
      { id: 'n3', defId: 'websocket-server', x: 400, y: 140, label: 'Chat Server A',   status: 'idle', config: { instances: 5, serviceRatePerInstance: 2000, maxConnections: 100000 } },
      { id: 'n4', defId: 'websocket-server', x: 400, y: 300, label: 'Chat Server B',   status: 'idle', config: { instances: 5, serviceRatePerInstance: 2000, maxConnections: 100000 } },
      { id: 'n5', defId: 'redis',            x: 600, y: 140, label: 'Session Store',   status: 'idle', config: { hitRate: 0.95, cacheSizeGB: 8 } },
      { id: 'n6', defId: 'kafka',            x: 600, y: 300, label: 'Message Queue',   status: 'idle', config: { partitions: 16, replicationFactor: 3 } },
      { id: 'n7', defId: 'cassandra',        x: 800, y: 220, label: 'Message Store',   status: 'idle', config: { instances: 3, serviceRatePerInstance: 3000 } },
      { id: 'n8', defId: 'microservice',     x: 980, y: 220, label: 'Presence Service',status: 'idle', config: { instances: 2, serviceRatePerInstance: 5000 } },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'WebSocket', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'WebSocket', animated: false },
      { id: 'e3', sourceId: 'n2', targetId: 'n4', protocol: 'WebSocket', animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'TCP',       animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n5', protocol: 'TCP',       animated: false },
      { id: 'e6', sourceId: 'n3', targetId: 'n6', protocol: 'AMQP',      animated: false },
      { id: 'e7', sourceId: 'n4', targetId: 'n6', protocol: 'AMQP',      animated: false },
      { id: 'e8', sourceId: 'n6', targetId: 'n7', protocol: 'TCP',       animated: false },
      { id: 'e9', sourceId: 'n3', targetId: 'n8', protocol: 'HTTP',      animated: false },
    ],
  },

  {
    id: 'xu-v1-search-autocomplete',
    name: 'Search Autocomplete',
    description: 'Trie-cache-backed typeahead: low-latency prefix lookups, DB only on cache miss.',
    category: 'Search',
    difficulty: 'Intermediate',
    group: 'alex-xu-v1',
    nodes: [
      { id: 'n1', defId: 'web-browser',   x: 60,  y: 220, label: 'Browser',         status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'api-gateway',   x: 220, y: 220, label: 'API Gateway',     status: 'idle', config: { instances: 2, serviceRatePerInstance: 5000 } },
      { id: 'n3', defId: 'app-server',    x: 400, y: 220, label: 'Autocomplete Svc',status: 'idle', config: { instances: 4, serviceRatePerInstance: 3000 } },
      { id: 'n4', defId: 'redis',         x: 580, y: 140, label: 'Trie Cache',      status: 'idle', config: { hitRate: 0.9, cacheSizeGB: 16 } },
      { id: 'n5', defId: 'elasticsearch', x: 580, y: 300, label: 'Search Index',    status: 'idle', config: { instances: 3, serviceRatePerInstance: 1000 } },
      { id: 'n6', defId: 'mysql',         x: 780, y: 220, label: 'Query DB',        status: 'idle', config: { instances: 1, serviceRatePerInstance: 500 } },
      { id: 'n7', defId: 'worker',        x: 960, y: 220, label: 'Trie Builder',    status: 'idle', config: { instances: 2, serviceRatePerInstance: 100, jobIntervalSec: 3600 } },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'TCP',  animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'HTTP', animated: false },
      { id: 'e5', sourceId: 'n5', targetId: 'n6', protocol: 'TCP',  animated: false },
      { id: 'e6', sourceId: 'n7', targetId: 'n4', protocol: 'TCP',  animated: false },
      { id: 'e7', sourceId: 'n7', targetId: 'n6', protocol: 'TCP',  animated: false },
    ],
  },

  {
    id: 'xu-v1-video-streaming',
    name: 'Video Streaming (YouTube)',
    description: 'Upload → Transcoder → Object Storage → CDN. CDN offloads origin; Transcoder is async bottleneck.',
    category: 'Streaming',
    difficulty: 'Advanced',
    group: 'alex-xu-v1',
    nodes: [
      { id: 'n1', defId: 'web-browser',    x: 60,  y: 220, label: 'Client',           status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'cdn',            x: 220, y: 140, label: 'CDN Edge',         status: 'idle', config: { hitRate: 0.85, cacheSizeGB: 500 } },
      { id: 'n3', defId: 'api-gateway',    x: 220, y: 300, label: 'API Gateway',      status: 'idle', config: { instances: 2, serviceRatePerInstance: 5000 } },
      { id: 'n4', defId: 'app-server',     x: 420, y: 220, label: 'Video API',        status: 'idle', config: { instances: 4, serviceRatePerInstance: 1500 } },
      { id: 'n5', defId: 'kafka',          x: 620, y: 140, label: 'Upload Queue',     status: 'idle', config: { partitions: 8, replicationFactor: 3 } },
      { id: 'n6', defId: 'stream-processor',x: 820, y: 140, label: 'Transcoder',      status: 'idle', config: { instances: 8, serviceRatePerInstance: 50 } },
      { id: 'n7', defId: 'object-storage', x: 620, y: 300, label: 'Video Storage',    status: 'idle', config: {} },
      { id: 'n8', defId: 'mysql',          x: 820, y: 300, label: 'Metadata DB',      status: 'idle', config: { instances: 1, serviceRatePerInstance: 800 } },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n1', targetId: 'n3', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'HTTP', animated: false },
      { id: 'e4', sourceId: 'n4', targetId: 'n5', protocol: 'AMQP', animated: false },
      { id: 'e5', sourceId: 'n5', targetId: 'n6', protocol: 'AMQP', animated: false },
      { id: 'e6', sourceId: 'n6', targetId: 'n7', protocol: 'HTTP', animated: false },
      { id: 'e7', sourceId: 'n2', targetId: 'n7', protocol: 'HTTP', animated: false },
      { id: 'e8', sourceId: 'n4', targetId: 'n8', protocol: 'TCP',  animated: false },
    ],
  },

  {
    id: 'xu-v1-google-drive',
    name: 'Google Drive',
    description: 'File sync: Client → Block Storage + Metadata DB + Sync Worker. Upload/download with dedup.',
    category: 'Storage',
    difficulty: 'Advanced',
    group: 'alex-xu-v1',
    nodes: [
      { id: 'n1', defId: 'desktop-app',   x: 60,  y: 220, label: 'Desktop Client',  status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'load-balancer', x: 220, y: 220, label: 'Load Balancer',   status: 'idle', config: { instances: 2, serviceRatePerInstance: 5000 } },
      { id: 'n3', defId: 'app-server',    x: 400, y: 140, label: 'Upload Service',  status: 'idle', config: { instances: 4, serviceRatePerInstance: 500 } },
      { id: 'n4', defId: 'app-server',    x: 400, y: 300, label: 'Download Service',status: 'idle', config: { instances: 4, serviceRatePerInstance: 1000 } },
      { id: 'n5', defId: 'block-storage', x: 600, y: 140, label: 'Block Storage',   status: 'idle', config: {} },
      { id: 'n6', defId: 'mysql',         x: 600, y: 300, label: 'Metadata DB',     status: 'idle', config: { instances: 1, serviceRatePerInstance: 800 } },
      { id: 'n7', defId: 'worker',        x: 800, y: 220, label: 'Sync Worker',     status: 'idle', config: { instances: 5, serviceRatePerInstance: 200 } },
      { id: 'n8', defId: 'redis',         x: 980, y: 220, label: 'Session Cache',   status: 'idle', config: { hitRate: 0.9, cacheSizeGB: 4 } },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n2', targetId: 'n4', protocol: 'HTTP', animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'TCP',  animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n5', protocol: 'TCP',  animated: false },
      { id: 'e6', sourceId: 'n3', targetId: 'n6', protocol: 'TCP',  animated: false },
      { id: 'e7', sourceId: 'n4', targetId: 'n6', protocol: 'TCP',  animated: false },
      { id: 'e8', sourceId: 'n6', targetId: 'n7', protocol: 'TCP',  animated: false },
      { id: 'e9', sourceId: 'n7', targetId: 'n8', protocol: 'TCP',  animated: false },
    ],
  },

  // ── ALEX XU — VOLUME 2 ────────────────────────────────────────────────────────

  {
    id: 'xu-v2-proximity-service',
    name: 'Proximity Service',
    description: 'Geospatial search: Client → Geo Service → Geohash Cache → Geospatial DB. Find nearby POIs.',
    category: 'Location',
    difficulty: 'Intermediate',
    group: 'alex-xu-v2',
    nodes: [
      { id: 'n1', defId: 'mobile-app',    x: 60,  y: 220, label: 'Mobile Client',   status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'api-gateway',   x: 220, y: 220, label: 'API Gateway',     status: 'idle', config: { instances: 2, serviceRatePerInstance: 5000 } },
      { id: 'n3', defId: 'microservice',  x: 400, y: 220, label: 'Geo Service',     status: 'idle', config: { instances: 4, serviceRatePerInstance: 2000 } },
      { id: 'n4', defId: 'redis',         x: 580, y: 140, label: 'Geohash Cache',   status: 'idle', config: { hitRate: 0.8, cacheSizeGB: 8 } },
      { id: 'n5', defId: 'postgres',      x: 580, y: 300, label: 'Geospatial DB',   status: 'idle', config: { instances: 2, serviceRatePerInstance: 1000 } },
      { id: 'n6', defId: 'db-replica',    x: 780, y: 300, label: 'Read Replica',    status: 'idle', config: { instances: 2, serviceRatePerInstance: 1500 } },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'TCP',  animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'TCP',  animated: false },
      { id: 'e5', sourceId: 'n5', targetId: 'n6', protocol: 'TCP',  animated: false },
    ],
  },

  {
    id: 'xu-v2-nearby-friends',
    name: 'Nearby Friends',
    description: 'Real-time location sharing: WebSocket + Redis Pub/Sub fan-out. Location updates every 30s.',
    category: 'Real-time',
    difficulty: 'Advanced',
    group: 'alex-xu-v2',
    nodes: [
      { id: 'n1', defId: 'mobile-app',       x: 60,  y: 220, label: 'Mobile Client',   status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'load-balancer',    x: 220, y: 220, label: 'Load Balancer',   status: 'idle', config: { instances: 2, serviceRatePerInstance: 5000 } },
      { id: 'n3', defId: 'websocket-server', x: 400, y: 220, label: 'WebSocket Server',status: 'idle', config: { instances: 5, serviceRatePerInstance: 2000, maxConnections: 50000 } },
      { id: 'n4', defId: 'redis',            x: 600, y: 140, label: 'Redis Pub/Sub',   status: 'idle', config: { hitRate: 0.0, cacheSizeGB: 4 } },
      { id: 'n5', defId: 'microservice',     x: 600, y: 300, label: 'Geo Service',     status: 'idle', config: { instances: 3, serviceRatePerInstance: 2000 } },
      { id: 'n6', defId: 'cassandra',        x: 800, y: 220, label: 'Location History',status: 'idle', config: { instances: 3, serviceRatePerInstance: 3000 } },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'WebSocket', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'WebSocket', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'TCP',       animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'HTTP',      animated: false },
      { id: 'e5', sourceId: 'n5', targetId: 'n6', protocol: 'TCP',       animated: false },
    ],
  },

  {
    id: 'xu-v2-google-maps',
    name: 'Google Maps',
    description: 'Map tiles via CDN + Route computation service + Geospatial DB. Heavy read + compute workload.',
    category: 'Location',
    difficulty: 'Expert',
    group: 'alex-xu-v2',
    nodes: [
      { id: 'n1', defId: 'mobile-app',    x: 60,  y: 220, label: 'Mobile Client',   status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'cdn',           x: 220, y: 140, label: 'Tile CDN',        status: 'idle', config: { hitRate: 0.9, cacheSizeGB: 1000 } },
      { id: 'n3', defId: 'api-gateway',   x: 220, y: 300, label: 'API Gateway',     status: 'idle', config: { instances: 2, serviceRatePerInstance: 5000 } },
      { id: 'n4', defId: 'microservice',  x: 420, y: 140, label: 'Map Tile Service',status: 'idle', config: { instances: 4, serviceRatePerInstance: 2000 } },
      { id: 'n5', defId: 'microservice',  x: 420, y: 300, label: 'Route Service',   status: 'idle', config: { instances: 8, serviceRatePerInstance: 200 } },
      { id: 'n6', defId: 'redis',         x: 620, y: 140, label: 'Tile Cache',      status: 'idle', config: { hitRate: 0.85, cacheSizeGB: 32 } },
      { id: 'n7', defId: 'postgres',      x: 620, y: 300, label: 'Geo DB',          status: 'idle', config: { instances: 3, serviceRatePerInstance: 500 } },
      { id: 'n8', defId: 'object-storage',x: 820, y: 220, label: 'Map Tile Store',  status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n1', targetId: 'n3', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'HTTP', animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'HTTP', animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n6', protocol: 'TCP',  animated: false },
      { id: 'e6', sourceId: 'n5', targetId: 'n7', protocol: 'TCP',  animated: false },
      { id: 'e7', sourceId: 'n4', targetId: 'n8', protocol: 'HTTP', animated: false },
    ],
  },

  {
    id: 'xu-v2-distributed-message-queue',
    name: 'Distributed Message Queue',
    description: 'Kafka-style: Producer → LB → Broker cluster → ZooKeeper → Consumers → Storage. Partition-aware.',
    category: 'Messaging',
    difficulty: 'Expert',
    group: 'alex-xu-v2',
    nodes: [
      { id: 'n1', defId: 'api-client',    x: 60,  y: 220, label: 'Producer',        status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'load-balancer', x: 220, y: 220, label: 'Load Balancer',   status: 'idle', config: { instances: 2, serviceRatePerInstance: 10000 } },
      { id: 'n3', defId: 'kafka',         x: 400, y: 140, label: 'Broker 1',        status: 'idle', config: { partitions: 16, replicationFactor: 3, instances: 1, serviceRatePerInstance: 100000 } },
      { id: 'n4', defId: 'kafka',         x: 400, y: 300, label: 'Broker 2',        status: 'idle', config: { partitions: 16, replicationFactor: 3, instances: 1, serviceRatePerInstance: 100000 } },
      { id: 'n5', defId: 'zookeeper',     x: 600, y: 220, label: 'ZooKeeper',       status: 'idle', config: { instances: 3 } },
      { id: 'n6', defId: 'worker',        x: 800, y: 140, label: 'Consumer Group A',status: 'idle', config: { instances: 5, serviceRatePerInstance: 1000 } },
      { id: 'n7', defId: 'worker',        x: 800, y: 300, label: 'Consumer Group B',status: 'idle', config: { instances: 5, serviceRatePerInstance: 1000 } },
      { id: 'n8', defId: 'object-storage',x: 980, y: 220, label: 'Log Storage',     status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'TCP',  animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'TCP',  animated: false },
      { id: 'e3', sourceId: 'n2', targetId: 'n4', protocol: 'TCP',  animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'TCP',  animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n5', protocol: 'TCP',  animated: false },
      { id: 'e6', sourceId: 'n3', targetId: 'n6', protocol: 'AMQP', animated: false },
      { id: 'e7', sourceId: 'n4', targetId: 'n7', protocol: 'AMQP', animated: false },
      { id: 'e8', sourceId: 'n3', targetId: 'n8', protocol: 'TCP',  animated: false },
    ],
  },

  {
    id: 'xu-v2-metrics-monitoring',
    name: 'Metrics Monitoring & Alerting',
    description: 'Time-series ingestion: Agents → Queue → Stream Processor → TSDB → Alert Manager.',
    category: 'Observability',
    difficulty: 'Advanced',
    group: 'alex-xu-v2',
    nodes: [
      { id: 'n1', defId: 'iot-device',      x: 60,  y: 220, label: 'Metric Agents',   status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'kafka',           x: 240, y: 220, label: 'Ingestion Queue',  status: 'idle', config: { partitions: 16, replicationFactor: 3 } },
      { id: 'n3', defId: 'stream-processor',x: 440, y: 220, label: 'Stream Processor', status: 'idle', config: { instances: 4, serviceRatePerInstance: 5000 } },
      { id: 'n4', defId: 'cassandra',       x: 640, y: 140, label: 'TSDB',             status: 'idle', config: { instances: 3, serviceRatePerInstance: 2000 } },
      { id: 'n5', defId: 'alertmanager',    x: 640, y: 300, label: 'Alert Manager',    status: 'idle', config: {} },
      { id: 'n6', defId: 'grafana',         x: 840, y: 140, label: 'Grafana',          status: 'idle', config: {} },
      { id: 'n7', defId: 'redis',           x: 840, y: 300, label: 'Alert Cache',      status: 'idle', config: { hitRate: 0.7, cacheSizeGB: 2 } },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'TCP',  animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'AMQP', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'TCP',  animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'HTTP', animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n6', protocol: 'HTTP', animated: false },
      { id: 'e6', sourceId: 'n5', targetId: 'n7', protocol: 'TCP',  animated: false },
    ],
  },

  {
    id: 'xu-v2-ad-click-aggregation',
    name: 'Ad Click Event Aggregation',
    description: 'High-throughput click events: LB → Stream Processor → Queue → Aggregated DB. Burst-absorbing queue.',
    category: 'Analytics',
    difficulty: 'Advanced',
    group: 'alex-xu-v2',
    nodes: [
      { id: 'n1', defId: 'web-browser',     x: 60,  y: 220, label: 'Ad Client',       status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'load-balancer',   x: 220, y: 220, label: 'Load Balancer',   status: 'idle', config: { instances: 2, serviceRatePerInstance: 10000 } },
      { id: 'n3', defId: 'kafka',           x: 400, y: 220, label: 'Click Queue',     status: 'idle', config: { partitions: 16, replicationFactor: 3 } },
      { id: 'n4', defId: 'stream-processor',x: 600, y: 220, label: 'Aggregator',      status: 'idle', config: { instances: 6, serviceRatePerInstance: 3000 } },
      { id: 'n5', defId: 'cassandra',       x: 800, y: 140, label: 'Click DB',        status: 'idle', config: { instances: 3, serviceRatePerInstance: 2000 } },
      { id: 'n6', defId: 'redis',           x: 800, y: 300, label: 'Agg Cache',       status: 'idle', config: { hitRate: 0.75, cacheSizeGB: 8 } },
      { id: 'n7', defId: 'data-warehouse',  x: 980, y: 220, label: 'Data Warehouse',  status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'AMQP', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'AMQP', animated: false },
      { id: 'e4', sourceId: 'n4', targetId: 'n5', protocol: 'TCP',  animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n6', protocol: 'TCP',  animated: false },
      { id: 'e6', sourceId: 'n5', targetId: 'n7', protocol: 'TCP',  animated: false },
    ],
  },

  {
    id: 'xu-v2-hotel-reservation',
    name: 'Hotel Reservation System',
    description: 'Strong consistency booking: LB → App → Cache → DB + Payment. Serialised writes prevent double-booking.',
    category: 'E-commerce',
    difficulty: 'Advanced',
    group: 'alex-xu-v2',
    nodes: [
      { id: 'n1', defId: 'web-browser',   x: 60,  y: 220, label: 'Browser',          status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'load-balancer', x: 220, y: 220, label: 'Load Balancer',    status: 'idle', config: { instances: 2, serviceRatePerInstance: 5000 } },
      { id: 'n3', defId: 'app-server',    x: 400, y: 220, label: 'Booking Service',  status: 'idle', config: { instances: 4, serviceRatePerInstance: 800 } },
      { id: 'n4', defId: 'redis',         x: 580, y: 140, label: 'Availability Cache',status: 'idle', config: { hitRate: 0.75, cacheSizeGB: 4 } },
      { id: 'n5', defId: 'mysql',         x: 580, y: 300, label: 'Reservation DB',   status: 'idle', config: { instances: 1, serviceRatePerInstance: 600 } },
      { id: 'n6', defId: 'microservice',  x: 780, y: 220, label: 'Payment Service',  status: 'idle', config: { instances: 2, serviceRatePerInstance: 300 } },
      { id: 'n7', defId: 'kafka',         x: 960, y: 220, label: 'Booking Events',   status: 'idle', config: { partitions: 8, replicationFactor: 3 } },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'TCP',  animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'TCP',  animated: false },
      { id: 'e5', sourceId: 'n3', targetId: 'n6', protocol: 'HTTP', animated: false },
      { id: 'e6', sourceId: 'n6', targetId: 'n7', protocol: 'AMQP', animated: false },
    ],
  },

  {
    id: 'xu-v2-distributed-email',
    name: 'Distributed Email Service',
    description: 'Async email delivery: SMTP Gateway → Queue → Workers → Object Store for attachments.',
    category: 'Messaging',
    difficulty: 'Advanced',
    group: 'alex-xu-v2',
    nodes: [
      { id: 'n1', defId: 'api-client',    x: 60,  y: 220, label: 'Email API Client', status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'app-server',    x: 240, y: 220, label: 'SMTP Gateway',     status: 'idle', config: { instances: 3, serviceRatePerInstance: 2000 } },
      { id: 'n3', defId: 'sqs',           x: 440, y: 220, label: 'Email Queue',      status: 'idle', config: { maxQueueDepth: 100000 } },
      { id: 'n4', defId: 'worker',        x: 640, y: 140, label: 'Delivery Worker A',status: 'idle', config: { instances: 5, serviceRatePerInstance: 300 } },
      { id: 'n5', defId: 'worker',        x: 640, y: 300, label: 'Delivery Worker B',status: 'idle', config: { instances: 5, serviceRatePerInstance: 300 } },
      { id: 'n6', defId: 'mysql',         x: 840, y: 140, label: 'Email DB',         status: 'idle', config: { instances: 1, serviceRatePerInstance: 800 } },
      { id: 'n7', defId: 'object-storage',x: 840, y: 300, label: 'Attachment Store', status: 'idle', config: {} },
      { id: 'n8', defId: 'dead-letter',   x: 1020,y: 220, label: 'Dead Letter Queue',status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'AMQP', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'AMQP', animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'AMQP', animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n6', protocol: 'TCP',  animated: false },
      { id: 'e6', sourceId: 'n5', targetId: 'n7', protocol: 'HTTP', animated: false },
      { id: 'e7', sourceId: 'n4', targetId: 'n8', protocol: 'AMQP', animated: false },
    ],
  },

  {
    id: 'xu-v2-object-storage',
    name: 'S3-like Object Storage',
    description: 'Blob storage: Client → Metadata Service → Object Store + Replication Worker for durability.',
    category: 'Storage',
    difficulty: 'Expert',
    group: 'alex-xu-v2',
    nodes: [
      { id: 'n1', defId: 'api-client',    x: 60,  y: 220, label: 'Client',           status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'load-balancer', x: 220, y: 220, label: 'Load Balancer',    status: 'idle', config: { instances: 2, serviceRatePerInstance: 5000 } },
      { id: 'n3', defId: 'microservice',  x: 400, y: 140, label: 'Metadata Service', status: 'idle', config: { instances: 3, serviceRatePerInstance: 2000 } },
      { id: 'n4', defId: 'microservice',  x: 400, y: 300, label: 'Data Service',     status: 'idle', config: { instances: 5, serviceRatePerInstance: 500 } },
      { id: 'n5', defId: 'mysql',         x: 600, y: 140, label: 'Metadata DB',      status: 'idle', config: { instances: 1, serviceRatePerInstance: 800 } },
      { id: 'n6', defId: 'object-storage',x: 600, y: 300, label: 'Object Store',     status: 'idle', config: {} },
      { id: 'n7', defId: 'worker',        x: 800, y: 220, label: 'Replication Worker',status: 'idle', config: { instances: 3, serviceRatePerInstance: 200 } },
      { id: 'n8', defId: 'object-storage',x: 980, y: 220, label: 'Replica Store',    status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n2', targetId: 'n4', protocol: 'HTTP', animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'TCP',  animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n6', protocol: 'TCP',  animated: false },
      { id: 'e6', sourceId: 'n6', targetId: 'n7', protocol: 'TCP',  animated: false },
      { id: 'e7', sourceId: 'n7', targetId: 'n8', protocol: 'TCP',  animated: false },
    ],
  },

  {
    id: 'xu-v2-gaming-leaderboard',
    name: 'Real-time Gaming Leaderboard',
    description: 'Redis Sorted Set for live rankings: WebSocket push + async DB sync via Worker.',
    category: 'Gaming',
    difficulty: 'Intermediate',
    group: 'alex-xu-v2',
    nodes: [
      { id: 'n1', defId: 'mobile-app',       x: 60,  y: 220, label: 'Game Client',     status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'api-gateway',      x: 220, y: 220, label: 'API Gateway',     status: 'idle', config: { instances: 2, serviceRatePerInstance: 5000 } },
      { id: 'n3', defId: 'websocket-server', x: 400, y: 140, label: 'Score Server',    status: 'idle', config: { instances: 4, serviceRatePerInstance: 3000, maxConnections: 100000 } },
      { id: 'n4', defId: 'microservice',     x: 400, y: 300, label: 'Leaderboard API', status: 'idle', config: { instances: 3, serviceRatePerInstance: 5000 } },
      { id: 'n5', defId: 'redis',            x: 600, y: 220, label: 'Redis Sorted Set',status: 'idle', config: { hitRate: 0.95, cacheSizeGB: 8 } },
      { id: 'n6', defId: 'worker',           x: 800, y: 220, label: 'DB Sync Worker',  status: 'idle', config: { instances: 3, serviceRatePerInstance: 500 } },
      { id: 'n7', defId: 'mysql',            x: 980, y: 220, label: 'Score DB',        status: 'idle', config: { instances: 1, serviceRatePerInstance: 800 } },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP',      animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'WebSocket', animated: false },
      { id: 'e3', sourceId: 'n2', targetId: 'n4', protocol: 'HTTP',      animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'TCP',       animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n5', protocol: 'TCP',       animated: false },
      { id: 'e6', sourceId: 'n5', targetId: 'n6', protocol: 'TCP',       animated: false },
      { id: 'e7', sourceId: 'n6', targetId: 'n7', protocol: 'TCP',       animated: false },
    ],
  },

  {
    id: 'xu-v2-payment-system',
    name: 'Payment System',
    description: 'High-security payments: LB → Payment Gateway → Fraud Detection → Queue → DB. P99 ~500ms.',
    category: 'Fintech',
    difficulty: 'Expert',
    group: 'alex-xu-v2',
    nodes: [
      { id: 'n1', defId: 'web-browser',   x: 60,  y: 220, label: 'Client',           status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'load-balancer', x: 220, y: 220, label: 'Load Balancer',    status: 'idle', config: { instances: 2, serviceRatePerInstance: 5000 } },
      { id: 'n3', defId: 'microservice',  x: 400, y: 220, label: 'Payment Gateway',  status: 'idle', config: { instances: 4, serviceRatePerInstance: 500 } },
      { id: 'n4', defId: 'microservice',  x: 600, y: 140, label: 'Fraud Detection',  status: 'idle', config: { instances: 4, serviceRatePerInstance: 300 } },
      { id: 'n5', defId: 'redis',         x: 600, y: 300, label: 'Idempotency Cache',status: 'idle', config: { hitRate: 0.8, cacheSizeGB: 4 } },
      { id: 'n6', defId: 'sqs',           x: 800, y: 220, label: 'Payment Queue',    status: 'idle', config: { maxQueueDepth: 50000 } },
      { id: 'n7', defId: 'postgres',      x: 980, y: 220, label: 'Ledger DB',        status: 'idle', config: { instances: 2, serviceRatePerInstance: 400 } },
      { id: 'n8', defId: 'worker',        x: 1160,y: 220, label: 'Settlement Worker',status: 'idle', config: { instances: 3, serviceRatePerInstance: 200 } },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'HTTP', animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'TCP',  animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n6', protocol: 'AMQP', animated: false },
      { id: 'e6', sourceId: 'n6', targetId: 'n7', protocol: 'TCP',  animated: false },
      { id: 'e7', sourceId: 'n7', targetId: 'n8', protocol: 'TCP',  animated: false },
    ],
  },

  {
    id: 'xu-v2-digital-wallet',
    name: 'Digital Wallet',
    description: 'Transactional ledger: Wallet Service → append-only Ledger DB + async Notification. Strong consistency.',
    category: 'Fintech',
    difficulty: 'Expert',
    group: 'alex-xu-v2',
    nodes: [
      { id: 'n1', defId: 'mobile-app',    x: 60,  y: 220, label: 'Mobile Client',   status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'load-balancer', x: 220, y: 220, label: 'Load Balancer',   status: 'idle', config: { instances: 2, serviceRatePerInstance: 5000 } },
      { id: 'n3', defId: 'microservice',  x: 400, y: 220, label: 'Wallet Service',  status: 'idle', config: { instances: 4, serviceRatePerInstance: 600 } },
      { id: 'n4', defId: 'redis',         x: 580, y: 140, label: 'Balance Cache',   status: 'idle', config: { hitRate: 0.85, cacheSizeGB: 4 } },
      { id: 'n5', defId: 'postgres',      x: 580, y: 300, label: 'Ledger DB',       status: 'idle', config: { instances: 2, serviceRatePerInstance: 400 } },
      { id: 'n6', defId: 'kafka',         x: 780, y: 220, label: 'Transaction Events',status: 'idle', config: { partitions: 8, replicationFactor: 3 } },
      { id: 'n7', defId: 'worker',        x: 960, y: 220, label: 'Notification Worker',status: 'idle', config: { instances: 3, serviceRatePerInstance: 500 } },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'TCP',  animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'TCP',  animated: false },
      { id: 'e5', sourceId: 'n3', targetId: 'n6', protocol: 'AMQP', animated: false },
      { id: 'e6', sourceId: 'n6', targetId: 'n7', protocol: 'AMQP', animated: false },
    ],
  },

  {
    id: 'xu-v2-stock-exchange',
    name: 'Stock Exchange',
    description: 'Ultra-low latency: Client → Matching Engine → Order Queue → DB + WebSocket broadcast. Sub-ms P99.',
    category: 'Fintech',
    difficulty: 'Expert',
    group: 'alex-xu-v2',
    nodes: [
      { id: 'n1', defId: 'api-client',       x: 60,  y: 220, label: 'Trading Client',  status: 'idle', config: { rpsOverride: 1000 } },
      { id: 'n2', defId: 'load-balancer',    x: 220, y: 220, label: 'Load Balancer',   status: 'idle', config: { instances: 2, serviceRatePerInstance: 50000 } },
      { id: 'n3', defId: 'microservice',     x: 400, y: 220, label: 'Matching Engine', status: 'idle', config: { instances: 1, serviceRatePerInstance: 100000 } },
      { id: 'n4', defId: 'kafka',            x: 600, y: 140, label: 'Order Queue',     status: 'idle', config: { partitions: 32, replicationFactor: 3 } },
      { id: 'n5', defId: 'websocket-server', x: 600, y: 300, label: 'Market Data Feed',status: 'idle', config: { instances: 4, serviceRatePerInstance: 10000, maxConnections: 500000 } },
      { id: 'n6', defId: 'cassandra',        x: 800, y: 140, label: 'Order Book DB',   status: 'idle', config: { instances: 3, serviceRatePerInstance: 50000 } },
      { id: 'n7', defId: 'redis',            x: 800, y: 300, label: 'Price Cache',     status: 'idle', config: { hitRate: 0.99, cacheSizeGB: 2 } },
      { id: 'n8', defId: 'data-warehouse',   x: 980, y: 220, label: 'Trade Analytics', status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'TCP',       animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'TCP',       animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'AMQP',      animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'WebSocket', animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n6', protocol: 'TCP',       animated: false },
      { id: 'e6', sourceId: 'n3', targetId: 'n7', protocol: 'TCP',       animated: false },
      { id: 'e7', sourceId: 'n6', targetId: 'n8', protocol: 'TCP',       animated: false },
    ],
  },

  {
    id: 'preset-intermediate',
    name: 'Intermediate',
    description: 'Microservices + API gateway + service mesh for a scalable distributed system',
    category: 'Microservices',
    difficulty: 'Intermediate',
    group: 'complexity',
    nodes: [
      { id: 'n1', defId: 'web-browser',  x: 60,  y: 220, label: 'Browser',       status: 'idle', config: {} },
      { id: 'n2', defId: 'api-gateway',  x: 220, y: 220, label: 'API Gateway',   status: 'idle', config: {} },
      { id: 'n3', defId: 'service-mesh', x: 400, y: 220, label: 'Service Mesh',  status: 'idle', config: {} },
      { id: 'n4', defId: 'microservice', x: 580, y: 120, label: 'User Service',  status: 'idle', config: {} },
      { id: 'n5', defId: 'microservice', x: 580, y: 220, label: 'Order Service', status: 'idle', config: {} },
      { id: 'n6', defId: 'microservice', x: 580, y: 320, label: 'Billing Svc',   status: 'idle', config: {} },
      { id: 'n7', defId: 'redis',        x: 760, y: 160, label: 'Redis Cache',   status: 'idle', config: {} },
      { id: 'n8', defId: 'postgres',     x: 760, y: 300, label: 'PostgreSQL',    status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP',  animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP',  animated: false },
      { id: 'e3', sourceId: 'n3', targetId: 'n4', protocol: 'gRPC',  animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'gRPC',  animated: false },
      { id: 'e5', sourceId: 'n3', targetId: 'n6', protocol: 'gRPC',  animated: false },
      { id: 'e6', sourceId: 'n4', targetId: 'n7', protocol: 'TCP',   animated: false },
      { id: 'e7', sourceId: 'n5', targetId: 'n8', protocol: 'TCP',   animated: false },
      { id: 'e8', sourceId: 'n6', targetId: 'n8', protocol: 'TCP',   animated: false },
    ],
  },

  {
    id: 'preset-advanced',
    name: 'Advanced',
    description: 'Multi-region active-active with global load balancing and cross-region replication',
    category: 'Multi-region',
    difficulty: 'Expert',
    group: 'complexity',
    nodes: [
      { id: 'n1', defId: 'mobile-app',    x: 60,  y: 220, label: 'Global Client',    status: 'idle', config: {} },
      { id: 'n2', defId: 'anycast',       x: 220, y: 220, label: 'Anycast Router',   status: 'idle', config: {} },
      { id: 'n3', defId: 'load-balancer', x: 400, y: 140, label: 'LB Region US',     status: 'idle', config: {} },
      { id: 'n4', defId: 'load-balancer', x: 400, y: 300, label: 'LB Region EU',     status: 'idle', config: {} },
      { id: 'n5', defId: 'k8s-pod',       x: 580, y: 140, label: 'K8s Cluster US',   status: 'idle', config: {} },
      { id: 'n6', defId: 'k8s-pod',       x: 580, y: 300, label: 'K8s Cluster EU',   status: 'idle', config: {} },
      { id: 'n7', defId: 'cassandra',     x: 760, y: 140, label: 'Cassandra US',     status: 'idle', config: {} },
      { id: 'n8', defId: 'cassandra',     x: 760, y: 300, label: 'Cassandra EU',     status: 'idle', config: {} },
      { id: 'n9', defId: 'consul',        x: 940, y: 220, label: 'Global Discovery', status: 'idle', config: {} },
    ],
    edges: [
      { id: 'e1', sourceId: 'n1', targetId: 'n2', protocol: 'HTTP', animated: false },
      { id: 'e2', sourceId: 'n2', targetId: 'n3', protocol: 'HTTP', animated: false },
      { id: 'e3', sourceId: 'n2', targetId: 'n4', protocol: 'HTTP', animated: false },
      { id: 'e4', sourceId: 'n3', targetId: 'n5', protocol: 'HTTP', animated: false },
      { id: 'e5', sourceId: 'n4', targetId: 'n6', protocol: 'HTTP', animated: false },
      { id: 'e6', sourceId: 'n5', targetId: 'n7', protocol: 'TCP',  animated: false },
      { id: 'e7', sourceId: 'n6', targetId: 'n8', protocol: 'TCP',  animated: false },
      { id: 'e8', sourceId: 'n7', targetId: 'n8', protocol: 'TCP',  animated: false },
      { id: 'e9', sourceId: 'n5', targetId: 'n9', protocol: 'HTTP', animated: false },
    ],
  },
];

export interface CompareScenario {
  id: string;
  label: string;
  left: { label: string; rps: number; p99: number; errorRate: number; availability: number; cacheHit: number; dbLoad: number };
  right: { label: string; rps: number; p99: number; errorRate: number; availability: number; cacheHit: number; dbLoad: number };
}

export const COMPARE_SCENARIOS: CompareScenario[] = [
  {
    id: 'sync-vs-async',
    label: 'Sync vs Async',
    left: { label: 'Synchronous', rps: 2400, p99: 340, errorRate: 3.2, availability: 99.1, cacheHit: 0, dbLoad: 87 },
    right: { label: 'Asynchronous', rps: 8900, p99: 45, errorRate: 0.4, availability: 99.97, cacheHit: 0, dbLoad: 34 },
  },
  {
    id: 'no-cache-vs-cache',
    label: 'No Cache vs Cache',
    left: { label: 'No Cache', rps: 3200, p99: 280, errorRate: 1.8, availability: 99.4, cacheHit: 0, dbLoad: 94 },
    right: { label: 'With Redis Cache', rps: 14500, p99: 18, errorRate: 0.2, availability: 99.98, cacheHit: 82, dbLoad: 21 },
  },
  {
    id: 'single-vs-replicated',
    label: 'Single DB vs Replicated',
    left: { label: 'Single DB', rps: 4100, p99: 190, errorRate: 2.1, availability: 99.2, cacheHit: 45, dbLoad: 91 },
    right: { label: '1 Primary + 3 Replicas', rps: 11200, p99: 42, errorRate: 0.3, availability: 99.95, cacheHit: 45, dbLoad: 28 },
  },
  {
    id: 'single-vs-lb',
    label: 'Single Server vs LB',
    left: { label: 'Single Server', rps: 1800, p99: 420, errorRate: 5.4, availability: 97.8, cacheHit: 40, dbLoad: 72 },
    right: { label: 'Load Balanced (3x)', rps: 9600, p99: 65, errorRate: 0.6, availability: 99.94, cacheHit: 40, dbLoad: 31 },
  },
];